/**
 * AI Workshop LLC - Internationalization (i18n)
 * Supports: English (en), Spanish (es), Turkish (tr)
 */

export const translations = {
  en: {
    // Navigation & Common
    nav: {
      home: "Home",
      audit: "Free AI Audit",
      blog: "Blog",
      about: "About",
      contact: "Contact",
      getStarted: "Get Started",
      language: "Language",
    },

    // Landing Home Page
    home: {
      heroTitleLine1: "Your AI Implementation Plan",
      heroTitleLine2: "Personalized tool stack + 12-week roadmap — $149.",
      heroSubtitle:
        "Answer 8 questions. In about 5 minutes, our AI Implementation Engine turns your answers into a 20+ page AI Implementation Blueprint and 30‑day action plan for your Shopify-style store or online service business — with tools, costs, and timelines built from {{toolCount}}+ vetted AI tools and 2026 industry benchmarks.",
      heroGuarantee:
        "Get a clear, step‑by‑step AI plan — 60-Day Implementation Guarantee.",
      heroPill1: "Takes ~5 minutes",
      heroPill2: "Built for ecommerce & online services",
      heroPill3: "See impact in 2–4 weeks",
      heroPrimaryCta:
        "Start Your Free AI Implementation Blueprint",
      heroSecondaryCta: "See Example Blueprint",
      heroFinePrint:
        "No credit card for the audit • Full AI Implementation Blueprint $149 • 60‑Day Implementation Guarantee",
      heroLogosTitle: "Works alongside tools you already use",
      heroIndustriesTitle: "Especially powerful for",
      heroIndustryEcom: "Ecommerce / Shopify",
      heroIndustryHosp: "Hospitality",
      heroIndustryHealth: "Healthcare",
      heroIndustryRealEstate: "Real Estate",
      heroIndustryTech: "Online Services & Tech",

      statsAria: "Key statistics",
      statsImplLabel: "Implementations Analyzed",
      statsTimelineLabel: "Weeks Avg. Timeline",
      statsPriceLabel: "AI Implementation Blueprint",

      howTitle: "How Your AI Implementation Blueprint Works",
      howStep1Title: "Answer 8 Quick Questions",
      howStep1Desc:
        "Tell us about your business, platform (Shopify or otherwise), goals, and biggest challenges.",
      howStep2Title: "Our Engine Builds Your Blueprint",
      howStep2Desc:
        "The AI Implementation Engine maps your answers to proven ecommerce & service plays, tools, and a 30‑day plan.",
      howStep3Title: "Follow the Step‑by‑Step Plan",
      howStep3Desc:
        "Open your AI Implementation Blueprint and start executing. Use the prompts inside Grok, ChatGPT, Claude, or Gemini.",

      subscribeTitle:
        "Get Free AI Implementation Tips & Case Studies",
      subscribeSubtitle:
        "Get weekly AI implementation ideas for your business.",
      subscribePlaceholder: "you@business.com",
      subscribeButton: "Subscribe",
      subscribeFinePrint:
        "We respect your privacy. Unsubscribe anytime.",

      featuresBadge:
        "Your AI Implementation Blueprint Includes",
      featuresTitle: "Everything You Need to Start This Week",
      featuresSubtitle:
        "Not a generic checklist. A concrete, phased AI Implementation Blueprint with tools, setup guides, a model‑specific prompt library, and expected ROI—tailored to your industry, size, and budget.",
      featuresCta:
        "Build My AI Implementation Blueprint",

      painTitle: "Does this sound like you?",
      painSubtitle:
        "You’re not “behind.” You’re just busy running a business.",
      painOverwhelmedTitle: "Overwhelmed",
      painOverwhelmedDesc:
        "Everyone is talking about ChatGPT, Claude, Gemini, and Grok. You don't even know which one to use or where to start.",
      painOverwhelmedFixLabel: "The Fix:",
      painOverwhelmedFix:
        "We ask what you already use, then your AI Implementation Blueprint includes a model-specific prompt library and rollout plan around that stack.",
      painCostTitle: "Skeptical of Cost",
      painCostDesc:
        "You don’t have $5,000/month for fancy software. You need something that works without breaking the bank.",
      painCostFixLabel: "The Fix:",
      painCostFix:
        "Most Phase 1 tools cost $0–$49/mo. Your Blueprint shows exact pricing upfront and prioritizes quick wins you can afford.",
      painTimeTitle: "No Time to Learn",
      painTimeDesc:
        "You can’t spend 10 hours learning prompt engineering. You just want better emails, ads, and systems.",
      painTimeFixLabel: "The Fix:",
      painTimeFix:
        "Your Blueprint includes 15–30 copy‑paste prompts tailored to your business so you can plug them into your AI and start today.",

      founderTitle: "Founder’s Story",
      founderQuote:
        "I drove for Uber for 10 years and talked to thousands of small business owners. The #1 thing they all wanted wasn’t more tech — it was more time. That’s why I built the AI Implementation Blueprint.",
      founderName: "Ali Erdem",
      founderRole: "Founder, AI Workshop LLC",
      founderCta:
        "Chat with Ali about your business",

      pricingTitle:
        "Simple pricing, built for small businesses",
      pricingPriceLabel: "$149 one‑time",
      pricingBadge: "AI Implementation Blueprint",
      pricingItem1:
        "Personalized 3‑5 phase AI implementation roadmap",
      pricingItem2:
        "Tool stack with setup steps & pricing tailored to your stack",
      pricingItem3:
        "30‑day action plan with weekly tasks and simple KPIs",
      pricingItem4:
        "Custom prompt library (15–30 prompts) for Grok, ChatGPT, Claude, or Gemini",
      pricingItem5:
        "Risk analysis, guardrails & simple AI governance checklist",
      pricingItem6: "60‑Day Implementation Guarantee",
      pricingSecure: "Secure checkout via Stripe",

      testimonialsTitle: "What Owners Are Saying",
      testimonialsSubtitle:
        "Early access results from our beta program.",

      finalCtaTitle:
        "Ready to get your AI Implementation Blueprint?",
      finalCtaButton:
        "Start the Free Audit, See Your Blueprint",
      finalCtaSubtitle:
        "Takes ~5 minutes • See your AI Implementation Blueprint preview before you pay",

      // Meta
      metaTitle: "AI Blueprint for Your Business | AI Workshop",

      // Social proof
      socialProofText: "Built for SMB owners, ecommerce stores, and SaaS founders",

      // Hero bullets + extras
      heroBullet1: "Personalized to your business — not a generic checklist",
      heroBullet2: "Tools matched to your budget, platform, and problems",
      heroBullet3: "60-Day Implementation Guarantee",
      heroPrimaryCtaPrice: "Get My Blueprint · {price}",
      heroSecureLabel: "Secured by Stripe",

      // How it works
      howBadge: "How It Works",
      howSubtitle: "Three simple steps to your AI Implementation Blueprint.",
      howStep1Time: "Step 1",
      howStep2Time: "Step 2",
      howStep3Time: "Step 3",

      // Industries strip
      industriesStripLabel: "Industries we cover",
      industryEcom: "E-commerce",
      industryRestaurants: "Restaurants",
      industryHealthcare: "Healthcare",
      industryRealEstate: "Real Estate",
      industrySaaS: "SaaS",
      industryProfServices: "Professional Services",

      // Stats
      statsToolsLabel: "Vetted AI Tools",
      statsIndustriesLabel: "Industries Covered",

      // Features "What You Get"
      featuredMostValued: "Most Valued",
      wyg1Title: "12-Week Implementation Roadmap",
      wyg1Desc: "Phase-by-phase rollout plan matched to your team size and budget.",
      wyg1Stat: "Up to 8 hrs/week saved (based on tool vendor data)",
      wyg2Title: "{{toolCount}}+ Vetted AI Tools",
      wyg2Desc: "Handpicked tools with setup steps, pricing, and risk scores specific to your industry.",
      wyg2Stat: "Top 6 matched to you",
      wyg3Title: "Custom Prompt Library",
      wyg3Desc: "15–30 ready-to-use prompts for your AI assistant (Grok, ChatGPT, Claude, or Gemini).",
      wyg3Stat: "Works with any AI assistant",
      wyg4Title: "ROI Projections",
      wyg4Desc: "Year 1 ROI estimate and payback timeline based on your revenue and industry benchmarks.",
      wyg4Stat: "Projected Year 1 impact based on your revenue and tools",
      wyg5Title: "First 48 Hours Action Plan",
      wyg5Desc: "Exactly what to do today, tomorrow, and this week — no guessing required.",
      wyg5Stat: "First result in 2–7 days",
      wyg6Title: "60-Day Money-Back Guarantee",
      wyg6Desc: "Follow Phase 1 of your blueprint. If you don't see measurable value, we'll troubleshoot with you or refund you in full.",
      wyg6Stat: "Zero risk",

      // Testimonials (removed — awaiting real customer results)

      // Blueprint mockup tool names/badges
      mockupBlueprintLabel: "AI Implementation Blueprint",
      mockupTool1Name: "Klaviyo AI",
      mockupTool1Badge: "Email · $45/mo",
      mockupTool2Name: "Tidio Chat",
      mockupTool2Badge: "Support · Free",
      mockupTool3Name: "Triple Whale",
      mockupTool3Badge: "Analytics · $129/mo",

      // Pricing
      pricingSubtitle: "No subscriptions. No upsells. One blueprint, everything included.",
      pricingMostPopular: "Most Popular",
      pricingSavings: "one-time · no subscription",
      pricingCta: "Get My AI Blueprint",
      pricingIncludedLabel: "What's included:",
      pricingInc1Title: "3-Phase AI Roadmap",
      pricingInc1Sub: "Personalized to your industry and team",
      pricingInc2Title: "{{toolCount}}+ Tool Database",
      pricingInc2Sub: "Top 6 matched and ranked for you",
      pricingInc3Title: "Prompt Library",
      pricingInc3Sub: "15–30 prompts for your AI assistant",
      pricingInc4Title: "ROI Projections",
      pricingInc4Sub: "Year 1 impact and payback timeline",
      pricingInc5Title: "Risk Analysis",
      pricingInc5Sub: "Guardrails and governance checklist",
      pricingInc6Title: "Downloadable PDF",
      pricingInc6Sub: "Full report + action plan",

      // Comparison table
      cmpTitle: "How we compare",
      cmpSubtitle: "AI Workshop vs. hiring a consultant or doing it yourself",
      cmpColFeature: "Feature",
      cmpColConsultant: "Consultant",
      cmpColDiy: "DIY",
      cmpRow1: "Personalized to your business",
      cmpRow2: "Tool recommendations with pricing",
      cmpRow3: "Step-by-step setup guides",
      cmpRow4: "Custom prompt library",
      cmpRow5: "ROI projections",
      cmpRow6: "Money-back guarantee",
      cmpRowCost: "Total cost",
      cmpSometimes: "Sometimes",
      cmpDiyHours: "∞ hrs",
      cmpCta: "Get My Blueprint for {price}",

      // Live activity ticker (removed — no fabricated activity)
      trendEmerging: "Emerging",
      trendGrowing: "Growing",

      // New arrivals marquee
      newArrivalsBadge: "Tool Database Updated",
      newArrivalsTitle: "New AI tools added this week",
      newArrivalsSubtitle: "Our team reviews and adds new tools every week — your Blueprint always uses the latest data.",
      newArrivalsAdded: "Added",
      newArrivalsRecent: "Recently added",
      newArrivalsCtaLink: "Get your Blueprint with the latest tools",

      // Blueprint mockup
      mockupStoreLabel: "Your Business Name",
      mockupReportReady: "Report Ready",
      mockupYear1Roi: "Year 1 ROI",
      mockupSavedWeek: "Saved / Week",
      mockupRecommended: "Recommended",
      mockupTop3Tools: "Top 3 Tools For You",
      mockupMatch: "match",
      mockupImplProgress: "Blueprint Implementation Progress",
      mockupProgressDone: "1 of 6 done",
      mockup90DayRoadmap: "12-Week Roadmap",
      mockupPhase1: "Phase 1\nFoundation\nWks 1–4",
      mockupPhase2: "Phase 2\nGrowth\nWks 5–8",
      mockupPhase3: "Phase 3\nScale\nWks 9–12",
      mockupGeneratedBadge: "AI-Generated",
      mockupToolsEvaluated: "tools evaluated",

      // Final CTA extras
      finalCtaEngineLive: "Implementation Engine live · {count} tools",
      finalCtaCheck1: "No credit card required for audit",
      finalCtaCheck2: "Full Blueprint only {price}",
      finalCtaCheck3: "60-Day Implementation Guarantee",

      // Discover section
      discoverBadge: "Free to explore",
      discoverTitle: "Browse Our AI Tool Catalog",
      discoverDesc: "Explore our vetted AI tools, see this week's top pick from our engine, and build a sample stack — all free. When you're ready for the full personalized blueprint, we'll be here.",
      discoverCta: "Explore Tools",
      discoverStatTools: "AI tools",
      discoverStatIndustries: "Industries",
      discoverStatPick: "Engine pick",
      discoverStatPrice: "To browse",

      // Trust section
      trustLine1: "We're a new company building something we believe in.",
      trustLine2: "60-Day Implementation Guarantee — follow Phase 1 or full refund.",

      // Subscribe extras
      subscribeEmailLabel: "Email address",
      subscribeErrorEmail: "Please enter a valid email address.",
      subscribeError: "Something went wrong. Please try again.",
      subscribeSuccess: "You're subscribed! Check your inbox.",
    },

    // Hero Section (audit / inner pages)
    hero: {
      badge: "AI Adoption Engine v3.0",
      title: "Transform Your Business with AI",
      subtitle:
        "Get a personalized AI implementation plan built from {{toolCount}}+ vetted AI tools and 2026 McKinsey & Gartner benchmarks. Free audit, actionable insights.",
      cta: "Start Free AI Audit",
      ctaSecondary: "See How It Works",
      trusted: "Trusted by SMB owners across 12 industries",
    },

    // Audit multi‑step form
    audit: {
      required: "(required)",

      topBadge: "AI Implementation Audit · Step {{step}} of 4",
      topTitle: "Build your AI Implementation Blueprint",
      topSubtitle:
        "Answer a few focused questions and we’ll generate a 3‑phase AI roadmap, tool stack, prompt pack, and ROI projections tailored to your {{industry}}.",
      estimateMinutes: "2–3 minutes",
      industryFallback: "your industry",

      // Step 1
      step1Title: "Your business",
      step1Subtitle:
        "Help us understand who you are and what space you’re in so we can pull the right benchmarks.",
      fieldBusinessName: "Business name",
      placeholderBusinessName: "e.g., Coastal Home Supply",
      helperBusinessName:
        "Used for your report title and context.",
      fieldIndustry: "Industry",
      placeholderIndustry: "Select your industry",
      helperIndustry:
        "We’ll match you to SMB benchmarks for your space.",
      industryRetail: "Retail & E‑commerce",
      industryHospitality: "Hospitality & Restaurants",
      industryProf: "Professional Services",
      industryTech: "Technology & SaaS",
      industryFinance: "Finance & Banking",
      industryManufacturing: "Manufacturing",
      industryRealEstate: "Real Estate",
      industryEducation: "Education",
      industryOther: "Other",

      // Step 2
      step2Title: "Size, budget, and what you sell",
      step2Subtitle:
        "This helps us calibrate your roadmap so it’s realistic for your team and budget.",
      step2Group1Title: "Size and budget",
      fieldBusinessSize: "Business size",
      placeholderBusinessSize: "Select size",
      helperBusinessSize: "How many people are in your team?",
      sizeSolo: "Solo (1 person)",
      sizeSmall: "Small (2–10 employees)",
      sizeMedium: "Medium (11–50 employees)",
      sizeLarge: "Large (51+ employees)",

      fieldBudget: "AI budget",
      placeholderBudget: "Monthly budget",
      helperBudget:
        "Estimated monthly budget for AI tools and services.",
      budgetUnder200: "Under $200",
      budgetUnder500: "$200 – $500",
      budgetUnder2k: "$500 – $2,000",
      budget2k5k: "$2,000 – $5,000",
      budget5k10k: "$5,000 – $10,000",
      budget10k25k: "$10,000 – $25,000",
      budget25kPlus: "$25,000+",

      step2Group2Title:
        "What do you sell and which AI do you use?",
      fieldBusinessType: "Business type",
      placeholderBusinessType: "Select business type",
      helperBusinessType:
        "This helps us decide which growth levers to prioritize.",
      businessTypeEcom: "Ecommerce / Online store",
      businessTypeServices: "Services / Agency / Professional",
      businessTypeBoth: "Both products and services",

      fieldEcommercePlatform: "Main ecommerce platform",
      placeholderEcommercePlatform: "Select platform",
      helperEcommercePlatform:
        "We’ll tailor your Blueprint to fit this platform.",
      platformCustom: "Custom / Other",

      fieldAiAssistant: "Main AI assistant",
      placeholderAiAssistant:
        "Which AI assistant do you use most?",
      helperAiAssistant:
        "We’ll tune your prompt library to the AI you already use.",
      aiAssistantNone: "I don’t really use one yet",

      // Step 3
      step3Title: "Problems, goals, and current tools",
      step3Subtitle:
        "Tell us where you’re stuck and what a “win” looks like so we can prioritize the highest‑impact plays.",
      fieldProblems: "What are your top problems?",
      placeholderProblems:
        "e.g. \"I spend 4–5 hours every Friday manually copying invoices from QuickBooks into a spreadsheet, then chasing late payments by email one by one. It's killing my weekends and I've had 3 clients go 90+ days past due this quarter.\"\n\nThe more specific you are, the more your blueprint will feel like it was written just for you.",
      helperProblems:
        "Name the exact task, how long it takes, and what it's costing you. Specifics = a more personalized blueprint.",
      fieldGoals: "What are your goals?",
      placeholderGoals:
        "E.g., increase revenue, free up founder time, improve client experience…",
      fieldCurrentTools: "What tools do you already use?",
      placeholderCurrentTools:
        "List tools like Shopify, Toast, HubSpot, Notion, ClickUp, etc.",
      fieldEmail: "Where should we email your Blueprint?",
      placeholderEmail: "you@business.com",
      helperEmail:
        "We’ll email your AI Implementation Blueprint and preview link here.",

      howWeUseTitle: "How we use your answers",
      howWeUseIntro:
        "Your answers never get sold. We only use them to tune your AI Implementation Blueprint:",
      howWeUseItem1:
        "Match you to benchmarks from similar‑sized businesses.",
      howWeUseItem2:
        "Prioritize plays that fit your tools, budget, and team.",
      howWeUseItem3:
        "Generate prompts and examples that sound like your business.",

      priorityLabel:
        "Priority support: we’ll fast‑track your Blueprint and email you if we have clarifying questions.",

      // Step 4
      step4Title: "Review your answers",
      step4Subtitle:
        "Make sure everything looks right before we generate your AI Implementation Blueprint.",
      reviewBusiness: "Your business",
      reviewSize: "Team size:",
      reviewTypeBudget: "Business type & budget",
      reviewBudget: "AI budget:",
      reviewAiAssistant: "AI assistant:",
      reviewContact: "Contact & delivery",
      reviewPriority: "Priority support:",
      reviewProblems: "Your main problems",
      reviewGoals: "Your goals",
      reviewTools: "Your current tools",
      notSet: "Not specified",
      yes: "Yes",
      no: "No",

      // Suggestions / chips
      suggestedLabel: "Suggested",
      suggestedTools: "tools",
      suggestedGeneric: "items",
      clearChips: "Clear",
      loadingChips: "Loading smart suggestions…",

      // Buttons
      buttonCancel: "Cancel",
      buttonBack: "Back",
      buttonContinue: "Continue",
      buttonGenerate: "Generate My Blueprint",
      buttonGenerating: "Generating your Blueprint…",

      // Errors / validation
      errorBusinessName:
        "Business name must be at least 2 characters",
      errorIndustry: "Please select your industry",
      errorBusinessSize: "Please select your business size",
      errorBudget: "Please select your budget range",
      errorBusinessType: "Please select your business type",
      errorEcommercePlatform:
        "Please select your main sales platform",
      errorAiAssistant:
        "Please tell us which AI assistant you mainly use",
      errorProblems:
        "Describe your problems in more detail (at least 10 characters)",
      errorEmail: "Enter a valid email address",
      errorFillAll:
        "Please fill all required fields correctly",

      submitSuccess:
        "Success! Your AI Implementation Blueprint is ready — check your email!",
      submitGenericError:
        "Failed to generate report. Please try again.",
    },

    // Legacy single‑page form
    form: {
      title: "Free AI Audit",
      subtitle:
        "Get your personalized AI implementation plan in minutes",
      businessName: "Business Name",
      businessNamePlaceholder: "Enter your business name",
      industry: "Industry",
      industryPlaceholder: "Select your industry",
      businessSize: "Business Size",
      businessSizePlaceholder: "Select team size",
      budget: "AI Implementation Budget",
      budgetPlaceholder: "Select budget range",
      problems: "Current Challenges",
      problemsPlaceholder:
        "Describe your main business challenges...",
      goals: "Goals",
      goalsPlaceholder:
        "What do you want to achieve with AI?",
      currentTools: "Current Tools",
      currentToolsPlaceholder:
        "List tools you currently use (e.g., Shopify, QuickBooks)",
      email: "Email Address",
      emailPlaceholder: "Enter your email",
      submit: "Generate My AI Plan",
      submitting: "Analyzing your business...",
    },

    // Industries
    industries: {
      retail: "Retail / E-commerce",
      hospitality: "Restaurant / Hotel",
      healthcare: "Healthcare / Medical",
      professional: "Professional Services",
      technology: "Technology / SaaS",
      finance: "Finance / Banking",
      manufacturing: "Manufacturing",
      real_estate: "Real Estate",
      education: "Education",
      other: "Other",
    },

    // Business Sizes
    sizes: {
      solo: "Solo (Just me)",
      small: "2-5 employees",
      medium: "6-20 employees",
      large: "21-50 employees",
      enterprise: "50+ employees",
    },

    // Budget Ranges
    budgets: {
      under5k: "Under $5,000",
      "5k-10k": "$5,000 - $10,000",
      "10k-25k": "$10,000 - $25,000",
      "25k-50k": "$25,000 - $50,000",
      "50k+": "$50,000+",
    },

    // Report Preview (extended for localized preview page)
    preview: {
      title: "Your AI Implementation Preview",
      previewMode: "Preview Mode",
      unlockFull: "Unlock Full Report",
      price: "$149",
      executiveSummary: "Executive Summary",
      keyOpportunities: "Key Opportunities",
      viewedFor: "Viewed for",
      minutes: "minutes",
      includes: "Full report includes",
      includesList: [
        "4 Prioritized Action Steps with ROI",
        "AI Maturity Index & Velocity Score",
        "Industry Benchmarks from 18,000+ records",
        "Proprietary Blueprint Snippets",
        "SEO Optimization Kit",
        "PDF Download & Email Delivery",
      ],

      // New preview strings
      errorNotFound: "Report not found",
      errorInvalid:
        "Invalid report data received from server",
      errorGeneric:
        "Could not load report preview. Please try again.",
      errorTitle: "Error Loading Preview",
      businessFallback: "Business",
      metaDescription:
        "Preview your custom AI Implementation Blueprint for {{business}}.",
      moneyBackBadge: "🛡️ 60‑Day Implementation Guarantee",
      moneyBackLong:
        "🛡️ 60‑Day Implementation Guarantee — follow Phase 1 of your blueprint. If you don’t see value, we’ll troubleshoot or refund you in full.",
      moneyBackShort:
        "🛡️ 60‑Day Implementation Guarantee — follow Phase 1 or full refund",

      loading:
        "Building your AI Implementation Blueprint...",

      assistantFallback: "your AI assistant",

      bizDescShopify: "Shopify store owners",
      bizDescWoo: "online stores",
      bizDescBigcommerce: "ecommerce brands",
      bizDescSquarespace: "Squarespace shops",
      bizDescWix: "Wix stores",
      bizDescEcom: "ecommerce founders",
      bizDescServices: "service businesses",
      bizDescIndustryOwners: "{{industry}} owners",
      bizDescGeneric: "business owners",

      bonusHealthcareTitle: "HIPAA Compliance Checklist",
      bonusHealthcareDesc:
        "Pre‑vetted tools and workflows that keep patient data safe.",
      bonusFinanceTitle: "SOC2 & PCI‑DSS Guidelines",
      bonusFinanceDesc:
        "Audit‑ready configurations for financial services.",
      bonusEducationTitle: "FERPA‑Safe AI Tools",
      bonusEducationDesc:
        "Protect student data while automating admin tasks.",
      bonusGenericTitleFallback:
        "Your Industry AI Starter Pack",
      bonusGenericDesc:
        "Pre‑built prompts and templates specific to {{industry}}.",
      bonusYourBusiness: "your business",

      heroHeadline:
        "Your AI Implementation Blueprint for {{descriptor}}",
      forBusiness: "For {{business}}",
      yourBusiness: "Your Business",
      yourIndustry: "your industry",

      heroPillRoadmap:
        "✅ 3‑phase AI roadmap tailored to {{industry}}",
      heroPillPlaybooks:
        "✅ Done‑for‑you AI Playbooks (plug‑and‑play workflows)",
      heroPillChecklist:
        "✅ 90‑Day Adoption Checklist for your team",
      heroPillPrompts:
        "✅ Custom prompt library for {{assistant}}",

      blueprintSummary:
        "📄 Your blueprint: {{pages}} pages, {{tools}} tools, {{phases}} phases.",
      blueprintHiddenGems:
        "(including {{count}} hidden‑gem tools)",

      includesPlaybooks:
        "Includes {{count}} AI Playbooks",
      includesChecklist:
        "and a 90‑Day Adoption Checklist so your team knows exactly what to do each week.",

      projectedY1:
        "Projected Year 1 upside: {{amount}}",
      consultantValue:
        "A single AI consulting session runs $300–$500/hr. This blueprint covers what 4–6 hours of consulting would — for {{price}}, one-time.",
      paysForItself:
        "If it helps you unlock even {{smallWin}} in extra profit or savings this year, it pays for itself {{multiplier}}× over.",

      cohortProof:
        "Powered by 50k+ SMB records & 2025 McKinsey / Gartner / ICONIQ research",

      insideTimelineTitle:
        "Your 12‑Week AI Implementation Timeline",
      insideTimelineDesc:
        "Week‑by‑week plan with clear goals, deliverables, and milestones based on your answers — so you always know what to do first, next, and later.",
      insidePlaybooksTitle: "Done‑for‑You AI Playbooks",
      insidePlaybooksDesc:
        "Click‑and‑run workflows for your biggest bottlenecks — like {{p1}} and {{p2}}. Each playbook spells out tools, steps, owners, and expected outcome so you’re not staring at a blank screen.",
      insidePromptsTitle: "Custom Prompt Library",
      insidePromptsDesc:
        "Copy‑paste prompts for {{assistant}} written specifically for your top problems and the tools inside your stack — no generic “write a marketing plan” fluff.",
      insideChecklistTitle: "90‑Day Adoption Checklist",
      insideChecklistDesc:
        "A week‑by‑week checklist with owner, ops, and marketing tasks spelled out, so your team can implement the plan without being AI experts — and the blueprint doesn’t die in a PDF.",

      lockedRoadmapTitleBusiness:
        "{{businessName}}'s AI Implementation Blueprint is Ready",
      lockedRoadmapTitleGeneric:
        "Your Full AI Implementation Blueprint is Ready",
      lockedRoadmapDesc:
        "We’ve already built a complete 90‑day AI roadmap for {{business}} — including plug‑and‑play AI Playbooks and a 90‑Day Adoption Checklist so you and your team know exactly what to do each week.",
      lockedRoadmapValue:
        "One-time purchase — no subscription. Get your blueprint for {{price}} and keep it forever.",
      roadmapCardTitle:
        "Your AI Implementation Roadmap",
      phase1Title: "Phase 1: {{name}}",
      phase1Focus: "Focus: {{focus}}",
      phase2Title: "Phase 2: Scale & Automate",
      phase2Desc:
        "Integrate AI across departments.",
      phase3Title: "Phase 3: Optimize & Innovate",
      phase3Desc:
        "Fine‑tune and explore new opportunities.",

      lockedPromptsTitleBusiness:
        "{{businessName}}'s Custom Prompt Library",
      lockedPromptsTitleGeneric:
        "Your Custom Prompt Library",
      lockedPromptsDesc:
        "We’ve already written copy‑paste prompts for your top problems and the tools inside your playbooks — ready to drop into {{assistant}} today.",
      lockedPromptsValue:
        "💡 The prompt pack alone is something agencies charge hundreds for.",
      promptsCardTitle: "Your Custom Prompt Library",
      promptFor: "Prompt for: {{problem}}",

      bonusesTitle: "Exclusive Member Bonuses (Coming Soon)",
      bonusesSubtitle:
        "We're building these bonuses for you. As a launch customer, you'll get lifetime access when they're ready.",
      bonusBootcampTitle:
        "30‑Day Implementation Bootcamp",
      bonusBootcampDesc:
        "Daily emails guiding you through each step – from setup to first win.",
      bonusCommunityTitle: "Private Community Access",
      bonusCommunityDesc:
        "Connect with other founders implementing AI — ask questions, share wins.",
      bonusCoachTitle: "AI Coach Chatbot",
      bonusCoachDesc:
        "Get 24/7 answers to common implementation questions.",
      bonusDiscountsTitle: "Exclusive Tool Discounts",
      bonusDiscountsDesc:
        "Save on leading AI and automation tools featured in your blueprint.",
      bonusRefreshTitle: "Quarterly Blueprint Refresh",
      bonusRefreshDesc:
        "Your plan can refresh with new benchmarks and tools every 3 months.",

      researchBadge:
        "📊 Built on proprietary SMB data + external research (McKinsey, Gartner, ICONIQ 2025)",
      promoTitle: "Have a promo code?",
      promoLabel: "Promo code",
      promoPlaceholder: "Enter promo code",
      promoContinue: "Continue",

      testimonialsTitle:
        "What Business Owners Are Saying",
      testimonialsSubtitle:
        "Early access results from our beta AI Implementation Blueprint program.",
      // testimonials removed — awaiting real customer results

      faqTitle: "Frequently Asked Questions",
      faq1Q: "How quickly will I see results?",
      faq1A:
        "Most businesses can implement their first recommended tool within a week and start seeing improvements — like reduced manual work or faster customer responses — within the first month. The playbooks and checklist are designed for quick wins, not months of planning.",
      faq2Q: "Will my team actually use this?",
      faq2A:
        "Yes — instead of a vague strategy deck, you get concrete AI Playbooks and a 90‑Day Adoption Checklist. That means clear owners, weekly tasks, and simple steps your team can follow without being AI experts.",
      faq3Q: "What if AI doesn’t work for my business?",
      faq3A:
        "Our recommendations are based on patterns from thousands of businesses, but we know every business is unique. That's why we offer a 60‑day guarantee: if the blueprint doesn’t help you move forward, we’ll revise it or refund your purchase. You’re not stuck.",
      faq4Q: "How is this different from a generic AI guide?",
      faq4A:
        "This isn’t a one‑size‑fits‑all checklist. Every tool, prompt, playbook, and phase is selected based on your industry, business size, and the specific challenges you shared. It’s like having a consultant tailor a plan just for you — without the consulting fee.",

      exitTitle: "Not ready to buy yet?",
      exitSubtitle:
        "Get a free 3‑tip summary of your biggest AI opportunities delivered to your inbox.",
      exitEmailPlaceholder: "Enter your email",
      exitButton: "Send Tips",
      exitFinePrint:
        "We respect your privacy. Unsubscribe anytime.",

      unlockBusinessBlueprint:
        "Unlock {{businessName}}'s Blueprint",
      unlockMyBlueprint: "Unlock My AI Blueprint",
      unlockBusinessPrompts:
        "Unlock {{businessName}}'s Prompts",
      unlockPromptPack: "Unlock Prompt Pack",

      waitlistErrorEmail: "Enter your email",
      waitlistThanks:
        "Thanks! We’ll send you a free 3‑tip summary.",
      waitlistErrorGeneric:
        "Something went wrong. Please try again.",
      waitlistErrorNetwork:
        "Network error. Please try again.",
    },

    // Payment
    payment: {
      title: "Unlock Your Full AI Report",
      subtitle:
        "Get complete access to your personalized AI implementation plan",
      promoCode: "Have a promo code?",
      promoPlaceholder: "Enter code",
      apply: "Apply",
      payNow: "Pay Now",
      securePayment: "Secure payment powered by Stripe",
      moneyBack: "30-day money-back guarantee",
    },

    // Full Report
    report: {
      // old keys kept for backward compatibility where still used
      title: "Your AI Implementation Plan",
      preparedFor: "Prepared for",
      generatedOn: "Generated on",
      downloadPdf: "Download PDF",
      emailReport: "Email Report",
      tabs: {
        maturity: "Maturity",
        data: "Data",
        actions: "Actions",
        tools: "Tools",
        roi: "ROI",
        seo: "SEO Kit",
        services: "Services",
      },
      maturityIndex: "AI Maturity Index",
      maturityExplanation: "Your AI Maturity is",
      timeToValue: "Expected Time to Value",
      weeks: "weeks",
      scoreBreakdown: "Score Breakdown",
      riskFlags: "Personalized Risk Flags",
      actionSteps: "Prioritized Action Steps",
      impactScore: "Impact Score",
      expectedRoi: "Expected ROI",
      setupTime: "Setup Time",
      tool: "Tool",
      addressesProblem: "Addresses Your Problem",
      blueprint: "Proprietary Blueprint",
      toolSequence: "Conditional Tool Sequence",
      successKpis: "Success KPIs",
      roiProjections: "ROI Projections",
      monthlySavings: "Monthly Savings",
      annualImpact: "Annual Impact",
      paybackPeriod: "Payback Period",
      hoursSaved: "Hours Saved Weekly",
      industryBenchmarks: "Industry Benchmarks",
      aiRecommendation: "AI-Powered Recommendation",
      opportunityIdentified: "Opportunity Identified",
      expectedLift: "Expected Lift",

      // new keys for localized FullReportPage
      toasts: {
        copyError:
          "Failed to copy. You can select and copy manually.",
        shareSuccess:
          "Share link copied to clipboard!",
        shareError:
          "Could not copy share text. Please copy manually.",
        downloadUnexpected:
          "Download failed: server returned unexpected content.",
        downloadSuccess:
          "AI Implementation Blueprint downloaded!",
        downloadError:
          "Download failed. Try printing instead.",
      },
      share: {
        text:
          "Just got my AI Implementation Blueprint from AI Workshop LLC — check it out: {{url}}",
      },
      actions: {
        backToPreview: "Back to Preview",
        print: "Print",
        share: "Share",
        downloadPreparing: "Preparing download…",
        downloadTitle: "Download AI Implementation Blueprint",
        downloadCta: "Download Blueprint PDF",
      },
      pageTitle: "AI Implementation Blueprint",
      pageTitleWithName:
        "AI Implementation Blueprint | {{businessName}}",
      metaDescription:
        "Your AI Implementation Blueprint for {{businessName}} in the {{industry}} industry.",
      fullTabs: {
        tools: "Tools",
        diagnosis: "Diagnosis",
        prompts: "Prompts",
        risks: "Risks",
        market: "Market",
        departments: "Departments",
        financials: "Financials",
        competitive: "Competitive",
        governance: "Governance",
        guide: "Guide",
      },
      // First 48 Hours card
      first48: {
        title: "Your First 48 Hours",
        subtitle: "Zero fluff. Specific steps, specific tools. Start today.",
        badge: "Action Plan",
        day1: "Day 1 — Do Today",
        day2: "Day 2 — Do Tomorrow",
      },
      // You vs Your Industry benchmark block
      benchmark: {
        title: "You vs Your Industry",
        badge: "First-Mover Window Open",
        firstMoverDesc: "{pct}% of {industry} businesses haven't adopted AI yet — this is your window to outpace them before the market catches up.",
        adoptedLabel: "of your industry\nalready using AI",
        notAdoptedLabel: "haven't started\nyet",
        liftLabel: "avg lift from\ntop tool",
        sampleNote: "Based on {count} real businesses that implemented {toolName} — median revenue lift of +{pct}%.",
      },
      // Tools tab
      tools: {
        noTools: "No tool recommendations available yet.",
        summarySelected: "Tools Selected",
        summaryImplemented: "Implemented",
        summaryInProgress: "In Progress",
        summaryTopPicks: "🔥 Top Picks",
        setupGuide: "Setup Guide",
        gotchas: "Common Gotchas",
        successStory: "Real-World Result",
        whyPicked: "Why we picked this for {businessName}",
        pricing: "Pricing",
        integration: "Integration",
        alternatives: "Alternatives",
        statusNotStarted: "Not Started",
        statusInProgress: "In Progress",
        statusDone: "Done ✓",
        verifyPricing: "Verify current pricing",
        details: "Details",
        less: "Less",
      },
      // Diagnosis tab
      diagnosis: {
        title: "Your Business Pain Point Diagnosis",
        costOfInaction: "Estimated Cost of Inaction",
        priorityIssues: "Priority Issues",
        fixableProblems: "Fixable Problems",
        processing: "Diagnosis data is being processed. Please refresh.",
        solvedLabel: "solved by this plan",
        perMonth: "/month",
        calculating: "Calculating...",
      },
      // Prompts tab
      prompts: {
        title: "Your Custom Prompt Library",
        empty: "No prompts generated yet.",
        emptyDesc: "Complete your audit to unlock your Prompt Operating System.",
        export: "Export All Plays",
        copy: "Copy Ready‑to‑Run Prompt",
        copyPersonalized: "Copy Personalized Prompt",
        copied: "Copied!",
        searchPlaceholder: "Search by outcome, department, or keyword...",
        noResults: "No Prompt Plays match your filters.",
        clearFilters: "Clear filters",
        proTip: "Pro tip:",
        runIn: "Run this play in:",
        whyWorks: "Why this prompt works for businesses like yours",
        logOutcome: "Log your outcome (optional)",
      },
      // Financials tab
      financials: {
        total3Year: "Total 3‑Year Net Benefit",
        paybackPeriod: "Payback Period",
        projection: "3‑Year Financial Projection",
        tableYear: "Year",
        tableInvestment: "Investment",
        tableReturns: "Returns",
        tableNet: "Net Benefit",
        tableROI: "ROI",
        howYouCompare: "How You Compare",
        kpiTitle: "What to Measure Weekly",
        kpiDownload: "Download KPI Tracker (CSV)",
        sensitivity: "Sensitivity Analysis",
        cumulative: "Cumulative Net Benefit",
        peerBusinesses: "Businesses Like Yours Chose",
      },
      // Referral block
      referral: {
        heading: "You're now ahead of most {industry} businesses.",
        subheading: "Know another business owner who's worried about being left behind by AI? Send them here — this blueprint might be the thing that saves their business.",
        cta: "Share with a business owner",
        copied: "Link copied!",
        note: "No login needed · takes 5 minutes · $149 blueprint",
      },
    },

    // SEO Kit
    seo: {
      title: "SEO Optimization Kit",
      included: "Included",
      metaTags: "Meta Tags",
      titleTag: "Title Tag",
      metaDescription: "Meta Description",
      openGraph: "Open Graph Tags",
      headings: "Optimized Headings",
      mainHeadline: "H1 (Main Headline)",
      h2Suggestions: "H2 Suggestions",
      h3Suggestions: "H3 Suggestions",
      faqSchema: "FAQ Schema (Ready for SEO)",
      blogOutline: "Blog Post Outline (Generated)",
      suggestedTitle: "Suggested Title",
      outline: "Outline",
      targetKeywords: "Target Keywords",
      keywordResearch: "Keyword Research",
      primaryKeywords: "Primary Keywords",
      longTailKeywords: "Long-Tail Keywords",
      trendingTopics: "Trending Topics",
      internalLinking: "Internal Linking Suggestions",
    },

    // Footer
    footer: {
      tagline: "Transforming businesses with AI-powered insights",
      company: "Company",
      resources: "Resources",
      legal: "Legal",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      copyright:
        "© 2026 AI Workshop LLC. All rights reserved.",
    },

    // Common Actions
    actions: {
      submit: "Submit",
      cancel: "Cancel",
      back: "Back",
      next: "Next",
      loading: "Loading...",
      success: "Success!",
      error: "Error",
      tryAgain: "Try Again",
    },

    // Discover Page
    discover: {
      heroBadge: "Discover · Powered by the AI Workshop Tool Engine",
      heroTitleLine1: "One High-Leverage AI Move",
      heroTitleLine2: "Each Week",
      heroSubtitle:
        "Our proprietary Tool Engine analyzes live RSS signals, {{count}} vetted AI tools, and 2026 industry ROI evidence to surface one opinionated pick — not a directory of 300 apps.",
      heroPillSignals: "Live signal tracking",
      heroPillTools: "{{count}} vetted AI tools",
      heroPillMatching: "Problem-to-tool matching",

      pickSectionTitle: "This Week's Engine Pick",
      pickLoading: "Pulling this week's highest-leverage tool from our adoption database…",
      pickErrorBody: "Couldn't load this week's pick. Run your audit to get a full personalized stack.",
      pickErrorCta: "Start Your AI Audit",
      pickEmptyTitle: "Coming soon: your first AI Workshop Pick",
      pickEmptyBody:
        "We'll highlight one AI tool or automation play each week, tailored to SMBs, with concrete implementation steps.",
      pickWeek: "Week {{week}} · Engine Pick",
      pickTrendingUp: "Trending Up",
      pickUpdatedMonday: "Updated Monday",
      pickSmbScoreLabel: "SMB Score",
      pickEngineTake: "Engine's take this week",
      pickMetricPricing: "Pricing",
      pickMetricPricingDefault: "Varies",
      pickMetricSetup: "Setup",
      pickMetricSetupDefault: "Low–Medium",
      pickMetricSetupTime: "Setup Time",
      pickMetricClaimedRoi: "Claimed ROI",
      pickMetricLiveSignals: "Live Signals",
      pickBestFor: "Best for:",
      pickDefaultRole: "Owner / Operator",
      pickCtaFit: "See How It Fits Your Roadmap",
      pickCtaBlueprint: "Get My Full Blueprint",

      engineTitle: "How the Engine Picks",
      engineFootnote:
        "Pick is deterministic per ISO week (seed = year × 100 + week) · Rotates every Monday UTC",
      engineRssLabel: "Live RSS Signals",
      engineRssDesc:
        "Real-time adoption signals from industry feeds, product launches, and SMB forums",
      engineDataLabel: "SMB Adoption Data",
      engineDataDesc:
        "348+ vetted AI tools + 2026 McKinsey & Gartner benchmarks — what actually works by industry and size",
      engineMatchLabel: "Problem-to-Tool Match",
      engineMatchDesc:
        "Jaccard token scoring maps real SMB pain points to tool capabilities",
      engineRoiLabel: "ROI & Complexity Score",
      engineRoiDesc:
        "Each tool scored on evidence of ROI, setup ease, and integration fit",

      recentTitle: "Recently Discovered by Our Engine",
      recentBadge: "Live from RSS",
      recentRoiClaimed: "{{pct}}% ROI claimed",

      catalogTitle: "AI Workshop Tool Catalog",
      catalogBadge: "Ranked by SMB Score",
      catalogSearchPlaceholder: "Search tools by name…",
      catalogIndustryAll: "All",
      catalogIndustryRetail: "Retail",
      catalogIndustryHealthcare: "Healthcare",
      catalogIndustryFinance: "Finance",
      catalogIndustryHospitality: "Hospitality",
      catalogIndustryManufacturing: "Manufacturing",
      catalogIndustryLegal: "Legal",
      catalogEmpty: "No tools found for this filter.",
      catalogBadgeGrowing: "Growing",
      catalogBadgeSetup: "{{level}} setup",
      catalogBadgeSignals: "{{count}} signal",
      catalogBadgeSignalsPlural: "{{count}} signals",
      catalogLinkVisit: "Visit",
      catalogLinkSearch: "Search",
      catalogAddToStack: "Add to stack",
      catalogRemoveFromStack: "Remove from stack",
      catalogAddAria: "Add {{name}} to stack",
      catalogRemoveAria: "Remove {{name}} from stack",

      catalogCtaTitle:
        "Browsing {{count}}+ tools is free. Knowing which ones fit YOUR business is the hard part.",
      catalogCtaBody:
        "Our engine scores every tool against your industry, problems, budget, and team size — then builds a step-by-step implementation plan with setup guides, ROI projections, and custom prompts. Takes 5 minutes.",
      catalogCtaButton: "Get My Personalized Blueprint",
      catalogCtaFine: "$149 one-time · 60-Day Implementation Guarantee",

      nudgeTitle: "Like what you see? Get recommendations matched to your business.",
      nudgeBody:
        "These are general tools ranked by SMB score. A personalized blueprint analyzes your specific industry, problems, and budget to recommend the right stack — with ROI projections, setup steps, and a 90-day plan.",
      nudgeCta: "Get My Personalized Blueprint",

      stackTitle: "Build Your Stack",
      stackSelected: "{{count}}/3 selected",
      stackComplete: "Stack complete!",
      stackQuickStart: "Quick start:",
      stackFullToast: "Stack is full — remove a tool first",
      stackEmptyLine1Prefix: "Click",
      stackEmptyLine1Suffix: "on any catalog tool, or use a quick-start template above",
      stackEmptyLine2: "Pick up to 3 tools — we'll estimate your 12-week impact",
      stackSynergy:
        "These tools integrate natively — your stack connects out of the box.",
      stackDiversityWarning:
        "All your tools cover the same area — consider adding one for automation or communication.",
      stackPhase1: "Weeks 1–4",
      stackPhase2: "Weeks 5–8",
      stackPhase3: "Weeks 9–12",
      stackSlotEmpty: "Empty — add a tool above",
      stackImpactTitle: "Estimated 12-Week Impact",
      stackImpactHours: "Hours/week saved",
      stackImpactValue: "12-week value",
      stackImpactValueTooltip:
        "Estimated at $35/hr avg labor rate · adjust for your business",
      stackImpactScore: "Avg SMB score",
      stackImpactComplexity: "Complexity",
      stackImpactNote:
        "* Estimate based on median SMB adoption data — actual results vary by business size and implementation.",
      stackFinalPrompt: "Want a personalized stack built for your exact business?",
      stackFinalCta: "Get My Full Blueprint",
      stackComplexityLow: "Low",
      stackComplexityMedium: "Medium",
      stackComplexityHigh: "High",

      tplRestaurantLabel: "Restaurant Quick Win",
      tplRestaurantT1Why: "Cuts no-shows by ~38% with automated SMS reminders.",
      tplRestaurantT2Why: "Auto-answers your top 10 FAQ so staff stay on the floor.",
      tplRestaurantT3Why: "One Monday email fills slow Tuesday nights.",
      tplEcomLabel: "E-commerce Growth",
      tplEcomT1Why: "Recovers ~15% of abandoned carts automatically.",
      tplEcomT2Why: "AI answers pre-sale questions 24/7, lifts conversions.",
      tplEcomT3Why: "SMS has 98% open rate vs 20% for email.",
      tplServiceLabel: "Service Business",
      tplServiceT1Why: "Free CRM that tracks every lead without spreadsheets.",
      tplServiceT2Why: "Eliminates scheduling back-and-forth entirely.",
      tplServiceT3Why: "Connects HubSpot + Calendly so nothing falls through.",
      tplDentalLabel: "Dental Practice",
      tplDentalT1Why: "Call + text + reminder hub — cuts no-shows 40–60%.",
      tplDentalT2Why: "Answers after-hours calls and books appointments 24/7.",
      tplDentalT3Why: "Reactivates lapsed patients automatically — 20–30% come back.",
      tplLegalLabel: "Solo Law Firm",
      tplLegalT1Why: "Case management + time tracking + billing in one place.",
      tplLegalT2Why: "Captures after-hours leads and qualifies them before callback.",
      tplLegalT3Why: "Reviews contracts 3–5× faster. Works inside MS Word.",
      tplAccountingLabel: "Accounting Firm",
      tplAccountingT1Why: "Central ledger — the bookkeeping spine everything plugs into.",
      tplAccountingT2Why: "AI reads receipts + invoices, auto-categorizes — saves 5–8 hrs/week.",
      tplAccountingT3Why: "Client comms + task automation + tax-season checklists.",
      tplHvacLabel: "HVAC Contractor",
      tplHvacT1Why: "Dispatch + invoicing + customer records — industry standard.",
      tplHvacT2Why: "Answers calls when you're on a roof. Books the dispatch automatically.",
      tplHvacT3Why: "Auto-requests reviews after every job — reputation compounds.",
      tplMedSpaLabel: "Med Spa / Salon",
      tplMedSpaT1Why: "Booking + client profiles + revenue reporting in one platform.",
      tplMedSpaT2Why: "Books appointments while you're with clients — handles rescheduling.",
      tplMedSpaT3Why: "Auto-rebooking + SMS campaigns + review requests.",

      valuePropsTitle1: "Your Clear AI Blueprint",
      valuePropsDesc1:
        "Stop guessing what to do first. Get a step-by-step action plan built from {{count}} vetted AI tools matched to your exact business profile.",
      valuePropsTitle2: "Proven Results in Weeks",
      valuePropsDesc2:
        "Most owners see measurable time savings and revenue impact within 4 weeks when they follow the phased plan.",
      valuePropsTitle3: "Built on Real Data",
      valuePropsDesc3:
        "Our Velocity Framework scores {{count}} AI tools against your profile using 2026 McKinsey & Gartner benchmarks to give you tools, timelines, and ROI projections that work.",
      valuePropsCta: "Get Your AI Blueprint",

      emailTitle: "Get the Engine Pick in Your Inbox",
      emailBody:
        "One vetted AI tool or automation play each week — plus where it fits in your roadmap. No spam, unsubscribe anytime.",
      emailPlaceholder: "you@yourbusiness.com",
      emailSubmit: "Join the Discover List",
      emailSubmitting: "Joining…",
      emailFine: "Sent every Monday · No spam · Unsubscribe anytime",
      emailSuccessToast: "You're on the Discover list! Check your inbox soon.",
      emailErrorToast: "Something went wrong — try again or email us directly.",
    },
  },

// ==================== SPANISH ====================

es: {
  nav: {
    home: "Inicio",
    audit: "Auditoría IA Gratis",
    blog: "Blog",
    about: "Nosotros",
    contact: "Contacto",
    getStarted: "Comenzar",
    language: "Idioma",
  },

  home: {
    heroTitleLine1: "Tu Plan de Implementación de IA",
    heroTitleLine2: "Stack de herramientas personalizado + hoja de ruta de 12 semanas — $149.",
    heroSubtitle:
      "Responde 8 preguntas. En unos 5 minutos, nuestro Motor de Implementación de IA convierte tus respuestas en un AI Implementation Blueprint de más de 40 páginas y un plan de acción de 30 días para tu tienda tipo Shopify o negocio de servicios online, con herramientas, costes y plazos basados en más de 50.000 patrones reales de PYMES.",
    heroGuarantee:
      "Obtén un plan de IA claro, paso a paso, o te devolvemos el dinero.",
    heroPill1: "Toma ~5 minutos",
    heroPill2:
      "Diseñado para ecommerce y servicios online",
    heroPill3: "Ver impacto en 2–4 semanas",
    heroPrimaryCta:
      "Iniciar tu AI Implementation Blueprint Gratis",
    heroSecondaryCta: "Ver Blueprint de Ejemplo",
    heroFinePrint:
      "Sin tarjeta para la auditoría • AI Implementation Blueprint completo $149 • Garantía de devolución de 60 días",
    heroLogosTitle:
      "Funciona junto a las herramientas que ya usas",
    heroIndustriesTitle: "Especialmente potente para",
    heroIndustryEcom: "Ecommerce / Shopify",
    heroIndustryHosp: "Hostelería",
    heroIndustryHealth: "Salud",
    heroIndustryRealEstate: "Bienes raíces",
    heroIndustryTech:
      "Servicios online y tecnología",

    statsAria: "Estadísticas clave",
    statsImplLabel: "Implementaciones analizadas",
    statsTimelineLabel:
      "Semanas de plazo promedio",
    statsPriceLabel: "AI Implementation Blueprint",

    howTitle:
      "Cómo funciona tu AI Implementation Blueprint",
    howStep1Title: "Responde 8 preguntas rápidas",
    howStep1Desc:
      "Cuéntanos sobre tu negocio, plataforma (Shopify u otra), objetivos y principales desafíos.",
    howStep2Title:
      "Nuestro motor construye tu Blueprint",
    howStep2Desc:
      "El Motor de Implementación de IA mapea tus respuestas a jugadas, herramientas y un plan de 30 días probados en ecommerce y servicios.",
    howStep3Title: "Sigue el plan paso a paso",
    howStep3Desc:
      "Abre tu AI Implementation Blueprint y ponte en marcha. Usa los prompts en Grok, ChatGPT, Claude o Gemini.",

    subscribeTitle:
      "Recibe tips y casos de estudio de implementación de IA",
    subscribeSubtitle:
      "Únete a 2.000+ dueños que reciben ideas semanales de implementación de IA para ecommerce y servicios online.",
    subscribePlaceholder: "tú@negocio.com",
    subscribeButton: "Suscribirme",
    subscribeFinePrint:
      "Respetamos tu privacidad. Puedes darte de baja cuando quieras.",

    featuresBadge:
      "Tu AI Implementation Blueprint incluye",
    featuresTitle:
      "Todo lo que necesitas para empezar esta semana",
    featuresSubtitle:
      "No es una checklist genérica. Es un AI Implementation Blueprint en fases con herramientas, guías de configuración, una librería de prompts específica para tu modelo y ROI esperado, adaptado a tu industria, tamaño y presupuesto.",
    featuresCta:
      "Crear mi AI Implementation Blueprint",

    painTitle: "¿Te suena esto?",
    painSubtitle:
      "No estás “atrasado”. Solo estás ocupado dirigiendo tu negocio.",
    painOverwhelmedTitle: "Abrumado",
    painOverwhelmedDesc:
      "Todo el mundo habla de ChatGPT, Claude, Gemini y Grok. No sabes ni cuál usar ni por dónde empezar.",
    painOverwhelmedFixLabel: "La solución:",
    painOverwhelmedFix:
      "Preguntamos qué ya usas y tu AI Implementation Blueprint incluye una librería de prompts específica para ese modelo y un plan de despliegue alrededor de ese stack.",
    painCostTitle: "Escéptico con el coste",
    painCostDesc:
      "No tienes $5.000/mes para software de lujo. Necesitas algo que funcione sin romper el presupuesto.",
    painCostFixLabel: "La solución:",
    painCostFix:
      "La mayoría de las herramientas de la Fase 1 cuestan $0–$49/mes. Tu Blueprint muestra los precios exactos por adelantado y prioriza victorias rápidas que puedes permitirte.",
    painTimeTitle: "Sin tiempo para aprender",
    painTimeDesc:
      "No puedes dedicar 10 horas a aprender prompt engineering. Solo quieres mejores emails, anuncios y sistemas.",
    painTimeFixLabel: "La solución:",
    painTimeFix:
      "Tu Blueprint incluye 15–30 prompts de copiar y pegar adaptados a tu negocio para que puedas usarlos hoy mismo.",

    founderTitle: "Historia del fundador",
    founderQuote:
      "Conduje para Uber durante 10 años y hablé con miles de dueños de pequeños negocios. Lo #1 que todos querían no era más tecnología, sino más tiempo. Por eso creé el AI Implementation Blueprint.",
    founderName: "Ali Erdem",
    founderRole: "Fundador, AI Workshop LLC",
    founderCta:
      "Habla con Ali sobre tu negocio",

    pricingTitle:
      "Precio sencillo, pensado para pequeños negocios",
    pricingPriceLabel: "$149 pago único",
    pricingBadge: "AI Implementation Blueprint",
    pricingItem1:
      "Roadmap de implementación de IA de 3–5 fases, personalizado",
    pricingItem2:
      "Stack de herramientas con pasos de configuración y precios adaptados a tu stack",
    pricingItem3:
      "Plan de acción de 30 días con tareas semanales y KPIs simples",
    pricingItem4:
      "Librería de prompts (15–30) para Grok, ChatGPT, Claude o Gemini",
    pricingItem5:
      "Análisis de riesgos, guardarraíles y checklist simple de gobernanza de IA",
    pricingItem6:
      "Garantía de devolución de dinero de 60 días",
    pricingSecure: "Pago seguro vía Stripe",

    testimonialsTitle:
      "Lo que dicen otros dueños",
    testimonialsSubtitle:
      "Resultados de acceso temprano de nuestro programa beta.",

    finalCtaTitle:
      "¿Listo para obtener tu AI Implementation Blueprint?",
    finalCtaButton:
      "Comienza la auditoría gratis y ve tu Blueprint",
    finalCtaSubtitle:
      "Toma ~5 minutos • Ve la vista previa de tu AI Implementation Blueprint antes de pagar",

    // Meta
    metaTitle: "Blueprint de IA para tu negocio | AI Workshop",

    // Social proof
    socialProofText: "Más de 127 dueños de PYMES confían en nosotros",

    // Hero bullets + extras
    heroBullet1: "Personalizado para tu negocio — no una lista genérica",
    heroBullet2: "Herramientas adaptadas a tu presupuesto, plataforma y problemas",
    heroBullet3: "Garantía de devolución de 60 días",
    heroPrimaryCtaPrice: "Obtener mi Blueprint · {price}",
    heroSecureLabel: "Pago seguro vía Stripe",

    // How it works
    howBadge: "Cómo funciona",
    howSubtitle: "Tres sencillos pasos hacia tu AI Implementation Blueprint.",
    howStep1Time: "Paso 1",
    howStep2Time: "Paso 2",
    howStep3Time: "Paso 3",

    // Industries strip
    industriesStripLabel: "Industrias que cubrimos",
    industryEcom: "E-commerce",
    industryRestaurants: "Restaurantes",
    industryHealthcare: "Salud",
    industryRealEstate: "Bienes raíces",
    industrySaaS: "SaaS",
    industryProfServices: "Servicios profesionales",

    // Stats
    statsToolsLabel: "Herramientas de IA revisadas",
    statsIndustriesLabel: "Industrias cubiertas",

    // Features "What You Get"
    featuredMostValued: "Más valorado",
    wyg1Title: "Hoja de ruta de implementación de 12 semanas",
    wyg1Desc: "Plan de despliegue por fases adaptado al tamaño de tu equipo y presupuesto.",
    wyg1Stat: "Promedio 8 h ahorradas/semana en fase 2",
    wyg2Title: "{{toolCount}}+ herramientas de IA revisadas",
    wyg2Desc: "Herramientas seleccionadas con pasos de configuración, precios y puntuaciones de riesgo para tu sector.",
    wyg2Stat: "Top 6 adaptadas a ti",
    wyg3Title: "Librería de prompts personalizada",
    wyg3Desc: "15–30 prompts listos para usar con tu asistente de IA (Grok, ChatGPT, Claude o Gemini).",
    wyg3Stat: "Funciona con cualquier asistente de IA",
    wyg4Title: "Proyecciones de ROI",
    wyg4Desc: "Estimación de ROI año 1 y plazo de recuperación basados en tus ingresos y benchmarks del sector.",
    wyg4Stat: "Impacto promedio $24K en año 1",
    wyg5Title: "Plan de acción para las primeras 48 horas",
    wyg5Desc: "Exactamente qué hacer hoy, mañana y esta semana — sin adivinar.",
    wyg5Stat: "Primeros resultados en 2–7 días",
    wyg6Title: "Garantía de devolución de 60 días",
    wyg6Desc: "Si no puedes identificar al menos una automatización que ahorre tiempo, te devolvemos el dinero.",
    wyg6Stat: "Sin riesgo",

    // Testimonials (removed — awaiting real customer results)

    // Blueprint mockup tool names/badges
    mockupBlueprintLabel: "AI Implementation Blueprint",
    mockupTool1Name: "Klaviyo AI",
    mockupTool1Badge: "Email · $45/mes",
    mockupTool2Name: "Tidio Chat",
    mockupTool2Badge: "Soporte · Gratis",
    mockupTool3Name: "Triple Whale",
    mockupTool3Badge: "Analítica · $129/mes",

    // Pricing
    pricingSubtitle: "Sin suscripciones. Sin upsells. Un blueprint, todo incluido.",
    pricingMostPopular: "Más popular",
    pricingSavings: "pago único · sin suscripción",
    pricingCta: "Obtener mi AI Blueprint",
    pricingIncludedLabel: "¿Qué incluye?",
    pricingInc1Title: "Hoja de ruta de IA en 3 fases",
    pricingInc1Sub: "Personalizado a tu sector y equipo",
    pricingInc2Title: "Base de datos de {{toolCount}}+ herramientas",
    pricingInc2Sub: "Top 6 adaptadas y clasificadas para ti",
    pricingInc3Title: "Librería de prompts",
    pricingInc3Sub: "15–30 prompts para tu asistente de IA",
    pricingInc4Title: "Proyecciones de ROI",
    pricingInc4Sub: "Impacto año 1 y plazo de recuperación",
    pricingInc5Title: "Análisis de riesgos",
    pricingInc5Sub: "Guardarraíles y checklist de gobernanza",
    pricingInc6Title: "PDF descargable",
    pricingInc6Sub: "Informe completo + plan de acción",

    // Comparison table
    cmpTitle: "Cómo nos comparamos",
    cmpSubtitle: "AI Workshop vs. contratar un consultor o hacerlo tú mismo",
    cmpColFeature: "Característica",
    cmpColConsultant: "Consultor",
    cmpColDiy: "Por tu cuenta",
    cmpRow1: "Personalizado para tu negocio",
    cmpRow2: "Recomendaciones de herramientas con precios",
    cmpRow3: "Guías de configuración paso a paso",
    cmpRow4: "Librería de prompts personalizada",
    cmpRow5: "Proyecciones de ROI",
    cmpRow6: "Garantía de devolución",
    cmpRowCost: "Coste total",
    cmpSometimes: "A veces",
    cmpDiyHours: "∞ horas",
    cmpCta: "Obtener mi Blueprint por {price}",

    // Live activity ticker (removed — no fabricated activity)
    trendEmerging: "Emergente",
    trendGrowing: "En crecimiento",

    // New arrivals marquee
    newArrivalsBadge: "Base de herramientas actualizada",
    newArrivalsTitle: "Nuevas herramientas de IA añadidas esta semana",
    newArrivalsSubtitle: "Nuestro equipo revisa y añade nuevas herramientas cada semana — tu Blueprint siempre usa los datos más recientes.",
    newArrivalsAdded: "Añadida",
    newArrivalsRecent: "Añadida recientemente",
    newArrivalsCtaLink: "Obtén tu Blueprint con las últimas herramientas",

    // Blueprint mockup
    mockupStoreLabel: "Tu negocio",
    mockupReportReady: "Informe listo",
    mockupYear1Roi: "ROI año 1",
    mockupSavedWeek: "Ahorrado / sem.",
    mockupRecommended: "Recomendadas",
    mockupTop3Tools: "Top 3 herramientas para ti",
    mockupMatch: "coincidencia",
    mockupImplProgress: "Progreso de implementación del Blueprint",
    mockupProgressDone: "1 de 6 completado",
    mockup90DayRoadmap: "Hoja de Ruta 12 Semanas",
    mockupPhase1: "Fase 1\nBase\nSem. 1–4",
    mockupPhase2: "Fase 2\nCrecimiento\nSem. 5–8",
    mockupPhase3: "Fase 3\nEscala\nSem. 9–12",
    mockupGeneratedBadge: "Generado con IA",
    mockupToolsEvaluated: "herramientas evaluadas",

    // Final CTA extras
    finalCtaEngineLive: "Motor activo · {count} herramientas",
    finalCtaCheck1: "Sin tarjeta de crédito para la auditoría",
    finalCtaCheck2: "Blueprint completo solo {price}",
    finalCtaCheck3: "Garantía de devolución de 60 días",

    // Subscribe extras
    subscribeEmailLabel: "Dirección de correo",
    subscribeErrorEmail: "Por favor, introduce una dirección de correo válida.",
    subscribeError: "Algo salió mal. Por favor, inténtalo de nuevo.",
    subscribeSuccess: "¡Te has suscrito! Revisa tu bandeja de entrada.",
  },

  hero: {
    badge: "Motor de Adopción IA v3.0",
    title: "Transforma Tu Negocio con IA",
    subtitle:
      "Obtén un plan personalizado de implementación de IA basado en {{toolCount}}+ herramientas validadas y benchmarks de McKinsey y Gartner 2026. Auditoría gratuita, información accionable.",
    cta: "Iniciar Auditoría IA Gratis",
    ctaSecondary: "Ver Cómo Funciona",
    trusted: "Confiado por propietarios de PYMES en 12 industrias",
  },

  // Audit multi‑step
  audit: {
    required: "(requerido)",

    topBadge: "Auditoría de Implementación IA · Paso {{step}} de 4",
    topTitle: "Crea tu AI Implementation Blueprint",
    topSubtitle:
      "Responde unas pocas preguntas enfocadas y generaremos un roadmap de IA en 3 fases, stack de herramientas, paquete de prompts y proyecciones de ROI adaptados a tu {{industry}}.",
    estimateMinutes: "2–3 minutos",
    industryFallback: "tu industria",

    step1Title: "Tu negocio",
    step1Subtitle:
      "Ayúdanos a entender quién eres y en qué espacio juegas para usar los benchmarks correctos.",
    fieldBusinessName: "Nombre del negocio",
    placeholderBusinessName:
      "Ej.: Coastal Home Supply",
    helperBusinessName:
      "Lo usaremos para el título y contexto de tu informe.",
    fieldIndustry: "Industria",
    placeholderIndustry: "Selecciona tu industria",
    helperIndustry:
      "Te conectaremos con benchmarks de PYMES similares.",
    industryRetail: "Retail y E‑commerce",
    industryHospitality:
      "Hostelería y Restaurantes",
    industryProf: "Servicios Profesionales",
    industryTech: "Tecnología y SaaS",
    industryFinance: "Finanzas y Banca",
    industryManufacturing: "Manufactura",
    industryRealEstate: "Bienes Raíces",
    industryEducation: "Educación",
    industryOther: "Otra",

    step2Title:
      "Tamaño, presupuesto y qué vendes",
    step2Subtitle:
      "Esto nos ayuda a calibrar el roadmap para que sea realista para tu equipo y presupuesto.",
    step2Group1Title: "Tamaño y presupuesto",
    fieldBusinessSize: "Tamaño del negocio",
    placeholderBusinessSize:
      "Selecciona el tamaño",
    helperBusinessSize:
      "¿Cuántas personas hay en tu equipo?",
    sizeSolo: "Solo (1 persona)",
    sizeSmall: "Pequeño (2–10 empleados)",
    sizeMedium: "Mediano (11–50 empleados)",
    sizeLarge: "Grande (51+ empleados)",

    fieldBudget: "Presupuesto para IA",
    placeholderBudget: "Presupuesto mensual",
    helperBudget:
      "Presupuesto mensual estimado para herramientas y servicios de IA.",
    budgetUnder200: "Menos de $200",
    budgetUnder500: "$200 – $500",
    budgetUnder2k: "$500 – $2,000",
    budget2k5k: "$2,000 – $5,000",
    budget5k10k: "$5,000 – $10,000",
    budget10k25k: "$10,000 – $25,000",
    budget25kPlus: "$25,000+",

    step2Group2Title:
      "¿Qué vendes y qué IA usas?",
    fieldBusinessType: "Tipo de negocio",
    placeholderBusinessType:
      "Selecciona tipo de negocio",
    helperBusinessType:
      "Esto nos ayuda a priorizar las mejores palancas de crecimiento.",
    businessTypeEcom:
      "Ecommerce / Tienda online",
    businessTypeServices:
      "Servicios / Agencia / Profesional",
    businessTypeBoth:
      "Productos y servicios",

    fieldEcommercePlatform:
      "Plataforma principal de ecommerce",
    placeholderEcommercePlatform:
      "Selecciona la plataforma",
    helperEcommercePlatform:
      "Adaptaremos tu Blueprint para que encaje con esta plataforma.",
    platformCustom: "Personalizada / Otra",

    fieldAiAssistant:
      "Asistente de IA principal",
    placeholderAiAssistant:
      "¿Qué asistente de IA usas más?",
    helperAiAssistant:
      "Ajustaremos tu librería de prompts al modelo que ya usas.",
    aiAssistantNone:
      "Realmente aún no uso uno",

    step3Title:
      "Problemas, objetivos y herramientas actuales",
    step3Subtitle:
      "Cuéntanos dónde estás atascado y cómo se ve una “victoria” para priorizar los movimientos de mayor impacto.",
    fieldProblems:
      "¿Cuáles son tus principales problemas?",
    placeholderProblems:
      "Ej.: \"Paso 4–5 horas cada viernes copiando facturas de QuickBooks a una hoja de cálculo y persiguiendo pagos atrasados por correo. Ha habido 3 clientes con más de 90 días de retraso este trimestre.\"\n\nCuanto más específico seas, más personalizado será tu blueprint.",
    helperProblems:
      "Nombra la tarea exacta, cuánto tiempo toma y qué te está costando. Detalles = blueprint más personalizado.",
    fieldGoals: "¿Cuáles son tus objetivos?",
    placeholderGoals:
      "Ej.: aumentar ingresos, liberar tiempo del fundador, mejorar la experiencia del cliente…",
    fieldCurrentTools:
      "¿Qué herramientas ya usas?",
    placeholderCurrentTools:
      "Lista herramientas como Shopify, Toast, HubSpot, Notion, ClickUp, etc.",
    fieldEmail:
      "¿A qué correo te enviamos el Blueprint?",
    placeholderEmail: "tú@negocio.com",
    helperEmail:
      "Te enviaremos tu AI Implementation Blueprint y el enlace de vista previa aquí.",

    howWeUseTitle:
      "Cómo usamos tus respuestas",
    howWeUseIntro:
      "Tus respuestas nunca se venden. Solo las usamos para ajustar tu AI Implementation Blueprint:",
    howWeUseItem1:
      "Conectarte con benchmarks de negocios de tamaño similar.",
    howWeUseItem2:
      "Priorizar jugadas que encajen con tus herramientas, presupuesto y equipo.",
    howWeUseItem3:
      "Generar prompts y ejemplos que suenen a tu negocio.",

    priorityLabel:
      "Soporte prioritario: aceleramos tu Blueprint y te escribimos si necesitamos aclarar algo.",

    step4Title:
      "Revisa tus respuestas",
    step4Subtitle:
      "Asegúrate de que todo se ve bien antes de generar tu AI Implementation Blueprint.",
    reviewBusiness: "Tu negocio",
    reviewSize: "Tamaño del equipo:",
    reviewTypeBudget:
      "Tipo de negocio y presupuesto",
    reviewBudget: "Presupuesto IA:",
    reviewAiAssistant: "Asistente IA:",
    reviewContact: "Contacto y entrega",
    reviewPriority: "Soporte prioritario:",
    reviewProblems: "Tus principales problemas",
    reviewGoals: "Tus objetivos",
    reviewTools:
      "Tus herramientas actuales",
    notSet: "No especificado",
    yes: "Sí",
    no: "No",

    suggestedLabel: "Sugeridos",
    suggestedTools: "herramientas",
    suggestedGeneric: "elementos",
    clearChips: "Limpiar",
    loadingChips:
      "Cargando sugerencias inteligentes…",

    buttonCancel: "Cancelar",
    buttonBack: "Atrás",
    buttonContinue: "Continuar",
    buttonGenerate:
      "Generar mi Blueprint",
    buttonGenerating:
      "Generando tu Blueprint…",

    errorBusinessName:
      "El nombre del negocio debe tener al menos 2 caracteres",
    errorIndustry:
      "Selecciona tu industria",
    errorBusinessSize:
      "Selecciona el tamaño de tu negocio",
    errorBudget:
      "Selecciona tu rango de presupuesto",
    errorBusinessType:
      "Selecciona tu tipo de negocio",
    errorEcommercePlatform:
      "Selecciona tu plataforma principal de ventas",
    errorAiAssistant:
      "Indícanos qué asistente de IA usas principalmente",
    errorProblems:
      "Describe tus problemas con más detalle (al menos 10 caracteres)",
    errorEmail:
      "Ingresa un correo electrónico válido",
    errorFillAll:
      "Completa correctamente todos los campos requeridos",

    submitSuccess:
      "¡Listo! Tu AI Implementation Blueprint está listo — revisa tu correo.",
    submitGenericError:
      "No se pudo generar el informe. Inténtalo de nuevo.",
  },

  form: {
    title: "Auditoría IA Gratuita",
    subtitle:
      "Obtén tu plan personalizado de implementación de IA en minutos",
    businessName: "Nombre del Negocio",
    businessNamePlaceholder:
      "Ingresa el nombre de tu negocio",
    industry: "Industria",
    industryPlaceholder: "Selecciona tu industria",
    businessSize: "Tamaño del Negocio",
    businessSizePlaceholder:
      "Selecciona el tamaño del equipo",
    budget: "Presupuesto para IA",
    budgetPlaceholder:
      "Selecciona el rango de presupuesto",
    problems: "Desafíos Actuales",
    problemsPlaceholder:
      "Describe los principales desafíos de tu negocio...",
    goals: "Objetivos",
    goalsPlaceholder:
      "¿Qué quieres lograr con la IA?",
    currentTools: "Herramientas Actuales",
    currentToolsPlaceholder:
      "Lista las herramientas que usas actualmente (ej: Shopify, QuickBooks)",
    email: "Correo Electrónico",
    emailPlaceholder: "Ingresa tu correo",
    submit: "Generar Mi Plan de IA",
    submitting: "Analizando tu negocio...",
  },

  industries: {
    retail: "Retail / Comercio Electrónico",
    hospitality: "Restaurante / Hotel",
    healthcare: "Salud / Médico",
    professional: "Servicios Profesionales",
    technology: "Tecnología / SaaS",
    finance: "Finanzas / Banca",
    manufacturing: "Manufactura",
    real_estate: "Bienes Raíces",
    education: "Educación",
    other: "Otro",
  },

  sizes: {
    solo: "Solo (Solo yo)",
    small: "2-5 empleados",
    medium: "6-20 empleados",
    large: "21-50 empleados",
    enterprise: "50+ empleados",
  },

  budgets: {
    under5k: "Menos de $5,000",
    "5k-10k": "$5,000 - $10,000",
    "10k-25k": "$10,000 - $25,000",
    "25k-50k": "$25,000 - $50,000",
    "50k+": "$50,000+",
  },

  preview: {
    title:
      "Vista Previa de Tu Implementación de IA",
    previewMode: "Modo Vista Previa",
    unlockFull: "Desbloquear Informe Completo",
    price: "$149",
    executiveSummary: "Resumen Ejecutivo",
    keyOpportunities: "Oportunidades Clave",
    viewedFor: "Visto por",
    minutes: "minutos",
    includes: "El informe completo incluye",
    includesList: [
      "4 Pasos de Acción Priorizados con ROI",
      "Índice de Madurez IA y Puntuación de Velocidad",
      "Benchmarks de la Industria de 18,000+ registros",
      "Fragmentos de Blueprint Propietarios",
      "Kit de Optimización SEO",
      "Descarga PDF y Entrega por Email",
    ],

    errorNotFound: "Informe no encontrado",
    errorInvalid:
      "Datos de informe no válidos recibidos del servidor",
    errorGeneric:
      "No se pudo cargar la vista previa. Inténtalo de nuevo.",
    errorTitle:
      "Error al Cargar la Vista Previa",
    businessFallback: "Negocio",
    metaDescription:
      "Vista previa de tu AI Implementation Blueprint personalizado para {{business}}.",
    moneyBackBadge:
      "🛡️ Garantía de devolución de 60 días",
    moneyBackLong:
      "🛡️ Garantía de devolución de 60 días: prueba los playbooks y la checklist, y si no ayudan, te devolvemos el dinero. Sin preguntas.",
    moneyBackShort:
      "🛡️ Garantía de 60 días — reembolso completo, sin preguntas",

    loading:
      "Construyendo tu AI Implementation Blueprint...",

    assistantFallback: "tu asistente de IA",

    bizDescShopify: "dueños de tiendas Shopify",
    bizDescWoo: "tiendas online",
    bizDescBigcommerce: "marcas de ecommerce",
    bizDescSquarespace: "tiendas Squarespace",
    bizDescWix: "tiendas Wix",
    bizDescEcom: "fundadores de ecommerce",
    bizDescServices:
      "negocios de servicios",
    bizDescIndustryOwners:
      "dueños de {{industry}}",
    bizDescGeneric: "dueños de negocios",

    bonusHealthcareTitle:
      "Checklist de Cumplimiento HIPAA",
    bonusHealthcareDesc:
      "Herramientas y flujos pre‑validados que mantienen seguros los datos de pacientes.",
    bonusFinanceTitle:
      "Guía SOC2 y PCI‑DSS",
    bonusFinanceDesc:
      "Configuraciones listas para auditoría para servicios financieros.",
    bonusEducationTitle:
      "Herramientas de IA seguras para FERPA",
    bonusEducationDesc:
      "Protege los datos de estudiantes mientras automatizas tareas administrativas.",
    bonusGenericTitleFallback:
      "AI Starter Pack para tu industria",
    bonusGenericDesc:
      "Prompts y plantillas pre‑construidas específicas para {{industry}}.",
    bonusYourBusiness: "tu negocio",

    heroHeadline:
      "Tu AI Implementation Blueprint para {{descriptor}}",
    forBusiness: "Para {{business}}",
    yourBusiness: "Tu negocio",
    yourIndustry: "tu industria",

    heroPillRoadmap:
      "✅ Roadmap de IA de 3 fases adaptado a {{industry}}",
    heroPillPlaybooks:
      "✅ AI Playbooks hechos para ti (workflows plug‑and‑play)",
    heroPillChecklist:
      "✅ Checklist de Adopción de 90 días para tu equipo",
    heroPillPrompts:
      "✅ Librería de prompts personalizada para {{assistant}}",

    blueprintSummary:
      "📄 Tu blueprint: {{pages}} páginas, {{tools}} herramientas, {{phases}} fases.",
    blueprintHiddenGems:
      "(incluye {{count}} herramientas “joya”)",

    includesPlaybooks:
      "Incluye {{count}} AI Playbooks",
    includesChecklist:
      "y una Checklist de Adopción de 90 días para que tu equipo sepa qué hacer cada semana.",

    projectedY1:
      "Potencial estimado del Año 1: {{amount}}",
    consultantValue:
      "Una sesión de consultoría de IA cuesta $300–$500/hr. Este blueprint cubre lo que 4–6 horas de consultoría cubrirían — por {{price}}, pago único.",
    paysForItself:
      "Si te ayuda a desbloquear aunque sea {{smallWin}} en ganancias o ahorros extra este año, se paga solo {{multiplier}}×.",

    cohortProof:
      "Impulsado por 50k+ registros de PYMES y estudios de McKinsey / Gartner / ICONIQ 2025",

    insideTimelineTitle:
      "Tu Cronograma de Implementación de IA de 12 semanas",
    insideTimelineDesc:
      "Plan semana a semana con metas, entregables y hitos claros según tus respuestas — para que siempre sepas qué hacer primero, después y más adelante.",
    insidePlaybooksTitle:
      "AI Playbooks Hechos para Ti",
    insidePlaybooksDesc:
      "Workflows listos para ejecutar para tus mayores cuellos de botella — como {{p1}} y {{p2}}. Cada playbook detalla herramientas, pasos, responsables y resultado esperado para que no empieces de cero.",
    insidePromptsTitle:
      "Librería de Prompts Personalizada",
    insidePromptsDesc:
      "Prompts de copiar y pegar para {{assistant}} escritos específicamente para tus principales problemas y las herramientas de tu stack — nada de “escribe un plan de marketing” genérico.",
    insideChecklistTitle:
      "Checklist de Adopción de 90 días",
    insideChecklistDesc:
      "Checklist semana a semana con tareas para dirección, operaciones y marketing, para que tu equipo implemente el plan sin ser expertos en IA — y el blueprint no muera en un PDF.",

    lockedRoadmapTitleBusiness:
      "El AI Implementation Blueprint de {{businessName}} está listo",
    lockedRoadmapTitleGeneric:
      "Tu AI Implementation Blueprint completo está listo",
    lockedRoadmapDesc:
      "Ya hemos construido un roadmap de IA completo de 90 días para {{business}} — con AI Playbooks plug‑and‑play y una Checklist de Adopción de 90 días para que tú y tu equipo sepáis qué hacer cada semana.",
    lockedRoadmapValue:
      "Compra única — sin suscripción. Obtén tu blueprint por {{price}} y consérvalo para siempre.",
    roadmapCardTitle:
      "Tu Roadmap de Implementación de IA",
    phase1Title: "Fase 1: {{name}}",
    phase1Focus: "Enfoque: {{focus}}",
    phase2Title: "Fase 2: Escalar y Automatizar",
    phase2Desc:
      "Integra la IA en diferentes áreas.",
    phase3Title: "Fase 3: Optimizar e Innovar",
    phase3Desc:
      "Ajusta y explora nuevas oportunidades.",

    lockedPromptsTitleBusiness:
      "Librería de Prompts de {{businessName}}",
    lockedPromptsTitleGeneric:
      "Tu Librería de Prompts",
    lockedPromptsDesc:
      "Ya hemos escrito prompts de copiar y pegar para tus principales problemas y las herramientas de tus playbooks — listos para usar hoy mismo en {{assistant}}.",
    lockedPromptsValue:
      "💡 Solo el paquete de prompts es algo por lo que muchas agencias cobran cientos de dólares.",
    promptsCardTitle:
      "Tu Librería de Prompts Personalizada",
    promptFor: "Prompt para: {{problem}}",

    bonusesTitle:
      "Bonos Exclusivos (Próximamente)",
    bonusesSubtitle:
      "Estamos construyendo estos bonos para ti. Como cliente de lanzamiento, tendrás acceso de por vida cuando estén listos.",
    bonusBootcampTitle:
      "Bootcamp de Implementación de 30 días",
    bonusBootcampDesc:
      "Emails diarios guiándote paso a paso — desde la configuración hasta tu primera victoria.",
    bonusCommunityTitle:
      "Acceso a Comunidad Privada",
    bonusCommunityDesc:
      "Conecta con otros fundadores implementando IA — haz preguntas, comparte victorias.",
    bonusCoachTitle:
      "Chatbot Coach de IA",
    bonusCoachDesc:
      "Obtén respuestas 24/7 a preguntas comunes de implementación.",
    bonusDiscountsTitle:
      "Descuentos Exclusivos en Herramientas",
    bonusDiscountsDesc:
      "Ahorra en las principales herramientas de IA y automatización que aparecen en tu blueprint.",
    bonusRefreshTitle:
      "Actualización Trimestral del Blueprint",
    bonusRefreshDesc:
      "Tu plan puede actualizarse cada 3 meses con nuevos benchmarks y herramientas.",

    researchBadge:
      "📊 Basado en datos propietarios de PYMES + investigación externa (McKinsey, Gartner, ICONIQ 2025)",
    promoTitle: "¿Tienes un código promocional?",
    promoLabel: "Código promocional",
    promoPlaceholder: "Ingresa el código",
    promoContinue: "Continuar",

    testimonialsTitle:
      "Lo que dicen otros dueños",
    testimonialsSubtitle:
      "Resultados de acceso temprano de nuestro programa beta de AI Implementation Blueprint.",
    // testimonials removed — awaiting real customer results

    faqTitle: "Preguntas Frecuentes",
    faq1Q:
      "¿Qué tan rápido veré resultados?",
    faq1A:
      "La mayoría de los negocios pueden implementar su primera herramienta recomendada en una semana y empezar a ver mejoras — como menos trabajo manual o respuestas más rápidas — en el primer mes. Los playbooks y la checklist están diseñados para victorias rápidas, no meses de planificación.",
    faq2Q:
      "¿Mi equipo realmente usará esto?",
    faq2A:
      "Sí — en lugar de un deck de estrategia vago, obtienes AI Playbooks concretos y una Checklist de 90 días. Eso significa responsables claros, tareas semanales y pasos simples que tu equipo puede seguir sin ser expertos en IA.",
    faq3Q:
      "¿Y si la IA no funciona para mi negocio?",
    faq3A:
      "Nuestras recomendaciones se basan en patrones de miles de negocios, pero sabemos que cada empresa es única. Por eso ofrecemos una garantía de 60 días: si el blueprint no te ayuda a avanzar, lo revisamos o te devolvemos el dinero.",
    faq4Q:
      "¿En qué se diferencia esto de una guía genérica de IA?",
    faq4A:
      "Esto no es una checklist genérica. Cada herramienta, prompt, playbook y fase se elige según tu industria, tamaño y los desafíos específicos que compartiste. Es como tener un consultor que diseña un plan solo para ti — sin la tarifa de consultoría.",

    exitTitle: "¿Aún no quieres comprar?",
    exitSubtitle:
      "Recibe un resumen gratuito con 3 oportunidades clave de IA para tu negocio en tu correo.",
    exitEmailPlaceholder:
      "Ingresa tu correo electrónico",
    exitButton: "Enviar tips",
    exitFinePrint:
      "Respetamos tu privacidad. Puedes darte de baja cuando quieras.",

    unlockBusinessBlueprint:
      "Desbloquear el Blueprint de {{businessName}}",
    unlockMyBlueprint:
      "Desbloquear mi AI Blueprint",
    unlockBusinessPrompts:
      "Desbloquear los prompts de {{businessName}}",
    unlockPromptPack:
      "Desbloquear paquete de prompts",

    waitlistErrorEmail:
      "Ingresa tu correo",
    waitlistThanks:
      "¡Gracias! Te enviaremos un resumen gratuito con 3 tips.",
    waitlistErrorGeneric:
      "Algo salió mal. Inténtalo de nuevo.",
    waitlistErrorNetwork:
      "Error de red. Inténtalo de nuevo.",
  },

  payment: {
    title:
      "Desbloquea Tu Informe Completo de IA",
    subtitle:
      "Obtén acceso completo a tu plan personalizado de implementación de IA",
    promoCode: "¿Tienes un código promocional?",
    promoPlaceholder: "Ingresa el código",
    apply: "Aplicar",
    payNow: "Pagar Ahora",
    securePayment: "Pago seguro con Stripe",
    moneyBack:
      "Garantía de devolución de 30 días",
  },

  // UPDATED report block with full tab + playbooks/checklist/governance/guide
  report: {
    title: "Tu Plan de Implementación de IA",
    preparedFor: "Preparado para",
    generatedOn: "Generado el",
    downloadPdf: "Descargar PDF",
    emailReport: "Enviar por Email",
    tabs: {
      maturity: "Madurez",
      data: "Datos",
      actions: "Acciones",
      tools: "Herramientas",
      roi: "ROI",
      seo: "Kit SEO",
      services: "Servicios",
    },
    maturityIndex: "Índice de Madurez IA",
    maturityExplanation: "Tu Madurez IA es",
    timeToValue:
      "Tiempo Esperado para Valor",
    weeks: "semanas",
    scoreBreakdown:
      "Desglose de Puntuación",
    riskFlags:
      "Banderas de Riesgo Personalizadas",
    actionSteps:
      "Pasos de Acción Priorizados",
    impactScore: "Puntuación de Impacto",
    expectedRoi: "ROI Esperado",
    setupTime: "Tiempo de Configuración",
    tool: "Herramienta",
    addressesProblem:
      "Aborda Tu Problema",
    blueprint: "Blueprint Propietario",
    toolSequence:
      "Secuencia de Herramientas",
    successKpis: "KPIs de Éxito",
    roiProjections:
      "Proyecciones de ROI",
    monthlySavings: "Ahorro Mensual",
    annualImpact: "Impacto Anual",
    paybackPeriod:
      "Período de Recuperación",
    hoursSaved:
      "Horas Ahorradas Semanalmente",
    industryBenchmarks:
      "Benchmarks de la Industria",
    aiRecommendation:
      "Recomendación Impulsada por IA",
    opportunityIdentified:
      "Oportunidad Identificada",
    expectedLift: "Mejora Esperada",

    // NUEVO: toasts, share, actions, meta, pestañas completas
    toasts: {
      copyError:
        "No se pudo copiar. Puedes seleccionar y copiar manualmente.",
      shareSuccess:
        "¡Enlace para compartir copiado al portapapeles!",
      shareError:
        "No se pudo copiar el texto para compartir. Copia manualmente.",
      downloadUnexpected:
        "La descarga falló: el servidor devolvió contenido inesperado.",
      downloadSuccess:
        "¡AI Implementation Blueprint descargado!",
      downloadError:
        "La descarga falló. Intenta imprimir en su lugar.",
    },
    share: {
      text:
        "Acabo de recibir mi AI Implementation Blueprint de AI Workshop LLC — échale un vistazo: {{url}}",
    },
    actions: {
      backToPreview: "Volver a la vista previa",
      print: "Imprimir",
      share: "Compartir",
      downloadPreparing: "Preparando descarga…",
      downloadTitle:
        "Descargar AI Implementation Blueprint",
      downloadCta: "Descargar Blueprint en PDF",
    },
    pageTitle: "AI Implementation Blueprint",
    pageTitleWithName:
      "AI Implementation Blueprint | {{businessName}}",
    metaDescription:
      "Tu AI Implementation Blueprint para {{businessName}} en la industria de {{industry}}.",
    fullTabs: {
      tools: "Herramientas",
      diagnosis: "Diagnóstico",
      prompts: "Prompts",
      risks: "Riesgos",
      market: "Mercado",
      departments: "Departamentos",
      financials: "Finanzas",
      competitive: "Competencia",
      governance: "Gobernanza",
      guide: "Guía",
    },
    first48: {
      title: "Tus Primeras 48 Horas",
      subtitle: "Sin rodeos. Pasos específicos, herramientas específicas. Empieza hoy.",
      badge: "Plan de Acción",
      day1: "Día 1 — Hacer Hoy",
      day2: "Día 2 — Hacer Mañana",
    },
    benchmark: {
      title: "Tú vs Tu Sector",
      badge: "Ventana de Primer Movimiento Abierta",
      firstMoverDesc: "El {pct}% de los negocios de {industry} aún no han adoptado IA — esta es tu ventana para superarlos antes de que el mercado se ponga al día.",
      adoptedLabel: "de tu sector\nya usa IA",
      notAdoptedLabel: "todavía no\nhan empezado",
      liftLabel: "mejora media de\nherramienta principal",
      sampleNote: "Basado en {count} negocios reales que implementaron {toolName} — mejora mediana de ingresos del +{pct}%.",
    },
    tools: {
      noTools: "Aún no hay recomendaciones de herramientas.",
      summarySelected: "Herramientas Seleccionadas",
      summaryImplemented: "Implementadas",
      summaryInProgress: "En Progreso",
      summaryTopPicks: "🔥 Mejores Picks",
      setupGuide: "Guía de Configuración",
      gotchas: "Errores Comunes",
      successStory: "Resultado Real",
      whyPicked: "Por qué lo elegimos para {businessName}",
      pricing: "Precios",
      integration: "Integración",
      alternatives: "Alternativas",
      statusNotStarted: "Sin Empezar",
      statusInProgress: "En Progreso",
      statusDone: "Completado ✓",
      verifyPricing: "Verificar precios actuales",
      details: "Detalles",
      less: "Menos",
    },
    diagnosis: {
      title: "Diagnóstico de Puntos de Dolor de tu Negocio",
      costOfInaction: "Costo Estimado de la Inacción",
      priorityIssues: "Problemas Prioritarios",
      fixableProblems: "Problemas Solucionables",
      processing: "Los datos de diagnóstico se están procesando. Por favor actualiza.",
      solvedLabel: "resuelto por este plan",
      perMonth: "/mes",
      calculating: "Calculando...",
    },
    prompts: {
      title: "Tu Biblioteca de Prompts Personalizada",
      empty: "Aún no se han generado prompts.",
      emptyDesc: "Completa tu auditoría para desbloquear tu Sistema Operativo de Prompts.",
      export: "Exportar Todos los Plays",
      copy: "Copiar Prompt Listo para Usar",
      copyPersonalized: "Copiar Prompt Personalizado",
      copied: "¡Copiado!",
      searchPlaceholder: "Buscar por resultado, departamento o palabra clave...",
      noResults: "Ningún Prompt Play coincide con tus filtros.",
      clearFilters: "Limpiar filtros",
      proTip: "Consejo Pro:",
      runIn: "Ejecuta este play en:",
      whyWorks: "Por qué este prompt funciona para negocios como el tuyo",
      logOutcome: "Registra tu resultado (opcional)",
    },
    financials: {
      total3Year: "Beneficio Neto Total a 3 Años",
      paybackPeriod: "Período de Recuperación",
      projection: "Proyección Financiera a 3 Años",
      tableYear: "Año",
      tableInvestment: "Inversión",
      tableReturns: "Retornos",
      tableNet: "Beneficio Neto",
      tableROI: "ROI",
      howYouCompare: "Cómo te Comparas",
      kpiTitle: "Qué Medir Semanalmente",
      kpiDownload: "Descargar Tracker de KPI (CSV)",
      sensitivity: "Análisis de Sensibilidad",
      cumulative: "Beneficio Neto Acumulado",
      peerBusinesses: "Negocios Como el Tuyo Eligieron",
    },

    referral: {
      heading: "Ahora estás por delante de la mayoría de los negocios de {industry}.",
      subheading: "¿Conoces a otro dueño de negocio preocupado por quedarse atrás con la IA? Envíalo aquí — este blueprint puede ser lo que salve su negocio.",
      cta: "Compartir con un dueño de negocio",
      copied: "¡Enlace copiado!",
      note: "Sin registro · 5 minutos · Blueprint por $100",
    },

    // NUEVO: sección de playbooks
    playbooks: {
      title: "Tus AI Playbooks",
      subtitle:
        "Workflows de alto impacto que puedes lanzar en los próximos 30–90 días.",
      impactsDeptLabel:
        "Playbooks que impactan este departamento",
    },

    // NUEVO: checklist 90 días
    checklist: {
      title: "Checklist de Adopción de 90 días",
      subtitle:
        "Úsala como marcador semanal durante los primeros 3 meses.",
      categoryLabel: "Categoría",
      weekLabel: "Semana {{week}}",
    },

    // NUEVO: gobernanza
    governance: {
      bannerTitle: "Gobernanza y ética de IA",
      bannerSubtitle:
        "Marco de gobernanza adaptado al sector {{industry}}.",
      riskTitle: "Áreas de riesgo a vigilar",
      structureTitle: "Estructura de gobernanza",
      oversightBody: "Órgano de supervisión",
      departmentChampions: "Responsables por departamento",
      decisionFramework: "Marco de decisión",
    },

    // NUEVO: guía de implementación
    guide: {
      howToUsePlaybookLabel: "Playbook:",
      howToUsePlaybookText:
        "Asigna cada paso a un responsable, ponlo en el calendario y revisa el avance cada semana. Planifica los lunes, ejecuta de martes a jueves y revisa los viernes.",
      headerTitle: "Tu guía de implementación",
      headerSubtitle:
        "Sigue tu progreso semana a semana. Marca los pasos como completados y guardaremos tu avance automáticamente.",
      headerProgressLabel: "Progreso total",
      headerStepsLabel: "{{completed}}/{{total}} pasos",
      weekByWeekTitle: "Plan semana a semana",
      weekLabel: "Semana {{week}}",
      currentLabel: "Actual",
      anchoredIn: "Anclado en: {{phase}}",
      weeklyFocusFallback: "Implementar flujos clave",
      quarterlyReviewsTitle: "Revisiones trimestrales",
      suggestedKpisTitle: "KPIs sugeridos",
      exportCsv: "Exportar como CSV",
      shareProgress: "Compartir progreso (beta)",
      resourcesLabel: "Recursos",
      ownerLabel: "Responsable",
      estimatedTimeLabel: "Est.",
    },
  },

  seo: {
    title: "Kit de Optimización SEO",
    included: "Incluido",
    metaTags: "Meta Etiquetas",
    titleTag: "Etiqueta de Título",
    metaDescription:
      "Meta Descripción",
    openGraph: "Etiquetas Open Graph",
    headings: "Encabezados Optimizados",
    mainHeadline: "H1 (Titular Principal)",
    h2Suggestions: "Sugerencias H2",
    h3Suggestions: "Sugerencias H3",
    faqSchema:
      "Schema FAQ (Listo para SEO)",
    blogOutline:
      "Esquema de Artículo de Blog",
    suggestedTitle:
      "Título Sugerido",
    outline: "Esquema",
    targetKeywords:
      "Palabras Clave Objetivo",
    keywordResearch:
      "Investigación de Palabras Clave",
    primaryKeywords:
      "Palabras Clave Principales",
    longTailKeywords:
      "Palabras Clave Long-Tail",
    trendingTopics:
      "Temas en Tendencia",
    internalLinking:
      "Sugerencias de Enlaces Internos",
  },

  footer: {
    tagline:
      "Transformando negocios con información impulsada por IA",
    company: "Empresa",
    resources: "Recursos",
    legal: "Legal",
    privacy: "Política de Privacidad",
    terms: "Términos de Servicio",
    copyright:
      "© 2026 AI Workshop LLC. Todos los derechos reservados.",
  },

  actions: {
    submit: "Enviar",
    cancel: "Cancelar",
    back: "Atrás",
    next: "Siguiente",
    loading: "Cargando...",
    success: "¡Éxito!",
    error: "Error",
    tryAgain: "Intentar de Nuevo",
  },

  // Página Discover
  discover: {
    heroBadge: "Discover · Impulsado por el Tool Engine de AI Workshop",
    heroTitleLine1: "Una jugada de IA de alto impacto",
    heroTitleLine2: "cada semana",
    heroSubtitle:
      "Nuestro Tool Engine analiza señales RSS en vivo, {{count}} herramientas de IA validadas y evidencia de ROI 2026 del sector para destacar una recomendación con criterio — no un directorio de 300 apps.",
    heroPillSignals: "Señales en vivo",
    heroPillTools: "{{count}} herramientas validadas",
    heroPillMatching: "Match problema → herramienta",

    pickSectionTitle: "La elección del motor esta semana",
    pickLoading: "Extrayendo la herramienta de mayor impacto de nuestra base de adopción…",
    pickErrorBody: "No pudimos cargar la elección de esta semana. Haz tu auditoría y obtén un stack totalmente personalizado.",
    pickErrorCta: "Empieza tu auditoría de IA",
    pickEmptyTitle: "Próximamente: tu primera elección AI Workshop",
    pickEmptyBody:
      "Cada semana destacaremos una herramienta o jugada de automatización para pymes, con pasos concretos de implementación.",
    pickWeek: "Semana {{week}} · Elección del motor",
    pickTrendingUp: "En tendencia",
    pickUpdatedMonday: "Actualizado los lunes",
    pickSmbScoreLabel: "Puntaje PyME",
    pickEngineTake: "La lectura del motor esta semana",
    pickMetricPricing: "Precio",
    pickMetricPricingDefault: "Varía",
    pickMetricSetup: "Setup",
    pickMetricSetupDefault: "Bajo–Medio",
    pickMetricSetupTime: "Tiempo de setup",
    pickMetricClaimedRoi: "ROI reportado",
    pickMetricLiveSignals: "Señales en vivo",
    pickBestFor: "Ideal para:",
    pickDefaultRole: "Dueño / Operador",
    pickCtaFit: "Ver cómo encaja en tu hoja de ruta",
    pickCtaBlueprint: "Quiero mi blueprint completo",

    engineTitle: "Cómo elige el motor",
    engineFootnote:
      "La elección es determinista por semana ISO (semilla = año × 100 + semana) · Rota cada lunes UTC",
    engineRssLabel: "Señales RSS en vivo",
    engineRssDesc:
      "Señales de adopción en tiempo real de feeds del sector, lanzamientos y foros de pymes",
    engineDataLabel: "Datos de adopción PyME",
    engineDataDesc:
      "348+ herramientas validadas + benchmarks McKinsey y Gartner 2026 — lo que realmente funciona por industria y tamaño",
    engineMatchLabel: "Match problema → herramienta",
    engineMatchDesc:
      "Scoring con tokens Jaccard que vincula dolores reales de pymes con capacidades de cada herramienta",
    engineRoiLabel: "Puntaje ROI y complejidad",
    engineRoiDesc:
      "Cada herramienta puntuada por evidencia de ROI, facilidad de setup y encaje de integración",

    recentTitle: "Descubiertas hace poco por nuestro motor",
    recentBadge: "En vivo vía RSS",
    recentRoiClaimed: "{{pct}}% ROI reportado",

    catalogTitle: "Catálogo de herramientas AI Workshop",
    catalogBadge: "Ordenadas por puntaje PyME",
    catalogSearchPlaceholder: "Busca herramientas por nombre…",
    catalogIndustryAll: "Todas",
    catalogIndustryRetail: "Retail",
    catalogIndustryHealthcare: "Salud",
    catalogIndustryFinance: "Finanzas",
    catalogIndustryHospitality: "Hostelería",
    catalogIndustryManufacturing: "Manufactura",
    catalogIndustryLegal: "Legal",
    catalogEmpty: "No hay herramientas para este filtro.",
    catalogBadgeGrowing: "Creciendo",
    catalogBadgeSetup: "Setup {{level}}",
    catalogBadgeSignals: "{{count}} señal",
    catalogBadgeSignalsPlural: "{{count}} señales",
    catalogLinkVisit: "Visitar",
    catalogLinkSearch: "Buscar",
    catalogAddToStack: "Agregar al stack",
    catalogRemoveFromStack: "Quitar del stack",
    catalogAddAria: "Agregar {{name}} al stack",
    catalogRemoveAria: "Quitar {{name}} del stack",

    catalogCtaTitle:
      "Navegar {{count}}+ herramientas es gratis. Saber cuáles encajan con TU negocio es la parte difícil.",
    catalogCtaBody:
      "Nuestro motor puntúa cada herramienta contra tu industria, problemas, presupuesto y tamaño de equipo — y arma un plan de implementación paso a paso con guías, proyecciones de ROI y prompts personalizados. Lleva 5 minutos.",
    catalogCtaButton: "Quiero mi blueprint personalizado",
    catalogCtaFine: "$149 pago único · Garantía de implementación de 60 días",

    nudgeTitle: "¿Te gusta lo que ves? Recibe recomendaciones hechas para tu negocio.",
    nudgeBody:
      "Estas son herramientas generales ordenadas por puntaje PyME. Un blueprint personalizado analiza tu industria, problemas y presupuesto para recomendarte el stack correcto — con proyecciones de ROI, pasos de setup y un plan a 90 días.",
    nudgeCta: "Quiero mi blueprint personalizado",

    stackTitle: "Arma tu stack",
    stackSelected: "{{count}}/3 seleccionadas",
    stackComplete: "¡Stack completo!",
    stackQuickStart: "Inicio rápido:",
    stackFullToast: "El stack está lleno — quita una herramienta primero",
    stackEmptyLine1Prefix: "Haz clic en",
    stackEmptyLine1Suffix: "en cualquier herramienta del catálogo, o usa una plantilla rápida de arriba",
    stackEmptyLine2: "Elige hasta 3 herramientas — estimaremos tu impacto a 12 semanas",
    stackSynergy:
      "Estas herramientas se integran de forma nativa — tu stack conecta de entrada.",
    stackDiversityWarning:
      "Todas tus herramientas cubren la misma área — considera sumar una de automatización o comunicación.",
    stackPhase1: "Semanas 1–4",
    stackPhase2: "Semanas 5–8",
    stackPhase3: "Semanas 9–12",
    stackSlotEmpty: "Vacío — agrega una herramienta arriba",
    stackImpactTitle: "Impacto estimado a 12 semanas",
    stackImpactHours: "Horas/semana ahorradas",
    stackImpactValue: "Valor a 12 semanas",
    stackImpactValueTooltip:
      "Estimado a $35/h promedio de costo laboral · ajusta según tu negocio",
    stackImpactScore: "Puntaje PyME promedio",
    stackImpactComplexity: "Complejidad",
    stackImpactNote:
      "* Estimación basada en datos medianos de adopción de pymes — los resultados reales varían según el tamaño y la implementación.",
    stackFinalPrompt: "¿Quieres un stack personalizado hecho para tu negocio exacto?",
    stackFinalCta: "Quiero mi blueprint completo",
    stackComplexityLow: "Baja",
    stackComplexityMedium: "Media",
    stackComplexityHigh: "Alta",

    tplRestaurantLabel: "Victoria rápida: Restaurante",
    tplRestaurantT1Why: "Reduce los no-shows ~38% con recordatorios SMS automáticos.",
    tplRestaurantT2Why: "Responde tus 10 preguntas más frecuentes automáticamente para que el equipo no deje el salón.",
    tplRestaurantT3Why: "Un email del lunes llena los martes flojos.",
    tplEcomLabel: "Crecimiento e-commerce",
    tplEcomT1Why: "Recupera ~15% de carritos abandonados automáticamente.",
    tplEcomT2Why: "La IA responde preguntas de preventa 24/7 y sube la conversión.",
    tplEcomT3Why: "El SMS tiene 98% de apertura vs 20% del email.",
    tplServiceLabel: "Negocio de servicios",
    tplServiceT1Why: "CRM gratuito que registra cada lead sin planillas.",
    tplServiceT2Why: "Elimina por completo el ida y vuelta al agendar.",
    tplServiceT3Why: "Conecta HubSpot + Calendly para que no se escape nada.",
    tplDentalLabel: "Clínica Dental",
    tplDentalT1Why: "Hub de llamadas + SMS + recordatorios — reduce ausencias 40–60%.",
    tplDentalT2Why: "Contesta llamadas fuera de horario y agenda citas 24/7.",
    tplDentalT3Why: "Reactiva pacientes inactivos automáticamente — 20–30% regresan.",
    tplLegalLabel: "Despacho Jurídico",
    tplLegalT1Why: "Gestión de casos + horas facturables + facturación en un solo lugar.",
    tplLegalT2Why: "Captura leads fuera de horario y los califica antes de tu llamada.",
    tplLegalT3Why: "Revisa contratos 3–5× más rápido. Funciona dentro de MS Word.",
    tplAccountingLabel: "Firma Contable",
    tplAccountingT1Why: "Libro mayor central — la columna donde todo se conecta.",
    tplAccountingT2Why: "IA lee recibos y facturas, categoriza automáticamente — ahorra 5–8 hrs/semana.",
    tplAccountingT3Why: "Comunicación con clientes + automatización + checklists de temporada de impuestos.",
    tplHvacLabel: "Contratista HVAC",
    tplHvacT1Why: "Despacho + facturación + historial de clientes — estándar de la industria.",
    tplHvacT2Why: "Contesta llamadas cuando estás en un techo. Agenda el dispatch automáticamente.",
    tplHvacT3Why: "Solicita reseñas automáticamente después de cada trabajo — la reputación crece.",
    tplMedSpaLabel: "Med Spa / Salón",
    tplMedSpaT1Why: "Reservas + perfiles de clientes + reportes de ingresos en una plataforma.",
    tplMedSpaT2Why: "Agenda citas mientras atiendes clientes — maneja reprogramaciones.",
    tplMedSpaT3Why: "Re-reservas automáticas + campañas SMS + solicitudes de reseñas.",

    valuePropsTitle1: "Tu blueprint de IA, claro",
    valuePropsDesc1:
      "Deja de adivinar por dónde empezar. Recibe un plan de acción paso a paso construido con {{count}} herramientas de IA validadas y cruzadas con tu perfil exacto de negocio.",
    valuePropsTitle2: "Resultados probados en semanas",
    valuePropsDesc2:
      "La mayoría de dueños ve ahorro de tiempo e impacto en ingresos en 4 semanas cuando siguen el plan por fases.",
    valuePropsTitle3: "Construido con datos reales",
    valuePropsDesc3:
      "Nuestro Velocity Framework puntúa {{count}} herramientas de IA contra tu perfil usando benchmarks McKinsey y Gartner 2026 para darte herramientas, plazos y proyecciones de ROI que funcionan.",
    valuePropsCta: "Consigue tu blueprint de IA",

    emailTitle: "Recibe la elección del motor en tu inbox",
    emailBody:
      "Una herramienta o jugada de automatización validada cada semana — con su lugar en tu hoja de ruta. Sin spam, te das de baja cuando quieras.",
    emailPlaceholder: "tu@tunegocio.com",
    emailSubmit: "Sumarme a la lista Discover",
    emailSubmitting: "Sumándote…",
    emailFine: "Enviado cada lunes · Sin spam · Baja cuando quieras",
    emailSuccessToast: "¡Ya estás en la lista Discover! Revisa tu inbox pronto.",
    emailErrorToast: "Algo salió mal — intenta de nuevo o escríbenos directamente.",
  },
},

// ==================== TURKISH ====================

tr: {
  nav: {
    home: "Ana Sayfa",
    audit: "Ücretsiz AI Denetimi",
    blog: "Blog",
    about: "Hakkımızda",
    contact: "İletişim",
    getStarted: "Başla",
    language: "Dil",
  },

  home: {
    heroTitleLine1: "AI Uygulama Planınız",
    heroTitleLine2: "Kişiselleştirilmiş araç seti + 12 haftalık yol haritası — $149.",
    heroSubtitle:
      "8 soruyu cevaplayın. Yaklaşık 5 dakika içinde AI Uygulama Motorumuz, yanıtlarınızı Shopify tarzı mağazanız veya çevrimiçi hizmet işletmeniz için 20+ sayfalık bir AI Implementation Blueprint’e ve 30 günlük aksiyon planına dönüştürür; 50.000+ gerçek KOBİ örneğine dayalı araçlar, maliyetler ve zaman çizelgeleriyle.",
    heroGuarantee:
      "Adım adım, net bir AI planı alın ya da paranızı geri verelim.",
    heroPill1: "Yaklaşık 5 dakika sürer",
    heroPill2:
      "E‑ticaret ve çevrimiçi hizmetler için tasarlandı",
    heroPill3: "2–4 hafta içinde etkiyi görün",
    heroPrimaryCta:
      "Ücretsiz AI Implementation Blueprint’inizi Başlatın",
    heroSecondaryCta: "Örnek Blueprint’i Gör",
    heroFinePrint:
      "Denetim için kredi kartı yok • Tam AI Implementation Blueprint $149 • 60 günlük para iade garantisi",
    heroLogosTitle:
      "Zaten kullandığınız araçlarla birlikte çalışır",
    heroIndustriesTitle:
      "Özellikle şu alanlarda güçlü",
    heroIndustryEcom: "E‑ticaret / Shopify",
    heroIndustryHosp: "Yiyecek‑içecek / Otel",
    heroIndustryHealth: "Sağlık",
    heroIndustryRealEstate: "Gayrimenkul",
    heroIndustryTech:
      "Çevrimiçi hizmetler ve teknoloji",

    statsAria: "Temel istatistikler",
    statsImplLabel: "Analiz edilen uygulamalar",
    statsTimelineLabel:
      "Ortalama süre (hafta)",
    statsPriceLabel:
      "AI Implementation Blueprint",

    howTitle:
      "AI Implementation Blueprint’iniz nasıl çalışır",
    howStep1Title: "8 hızlı soruyu cevaplayın",
    howStep1Desc:
      "İşletmenizi, platformunuzu (Shopify veya diğer), hedeflerinizi ve en büyük zorluklarınızı anlatın.",
    howStep2Title:
      "Motorumuz Blueprint’inizi oluşturur",
    howStep2Desc:
      "AI Uygulama Motoru, yanıtlarınızı kanıtlanmış e‑ticaret ve hizmet senaryolarına, araçlara ve 30 günlük bir plana eşleştirir.",
    howStep3Title:
      "Adım adım planı uygulayın",
    howStep3Desc:
      "AI Implementation Blueprint’inizi açın ve uygulamaya başlayın. İçindeki prompt’ları Grok, ChatGPT, Claude veya Gemini’de kullanın.",

    subscribeTitle:
      "Ücretsiz AI uygulama ipuçları ve vaka çalışmaları alın",
    subscribeSubtitle:
      "E‑ticaret ve çevrimiçi hizmetler için her hafta AI uygulama fikirleri alan 2.000+ işletme sahibine katılın.",
    subscribePlaceholder: "siz@isletmeniz.com",
    subscribeButton: "Abone Ol",
    subscribeFinePrint:
      "Gizliliğinize saygı duyuyoruz. İstediğiniz zaman ayrılabilirsiniz.",

    featuresBadge:
      "AI Implementation Blueprint’inizde neler var",
    featuresTitle:
      "Bu hafta başlamak için ihtiyacınız olan her şey",
    featuresSubtitle:
      "Genel bir checklist değil. Araçlar, kurulum rehberleri, modele özel prompt kütüphanesi ve beklenen ROI ile aşamalı ve somut bir AI Implementation Blueprint; sektörünüze, büyüklüğünüze ve bütçenize göre uyarlanmış.",
    featuresCta:
      "AI Implementation Blueprint’imi Oluştur",

    painTitle:
      "Bunlardan biri size benziyor mu?",
    painSubtitle:
      "“Geri kalmış” değilsiniz. Sadece bir işletme yönetmekle meşgulsünüz.",
    painOverwhelmedTitle: "Bunalmış",
    painOverwhelmedDesc:
      "Herkes ChatGPT, Claude, Gemini ve Grok’tan bahsediyor. Hangisini kullanacağınızı veya nereden başlayacağınızı bilmiyorsunuz.",
    painOverwhelmedFixLabel: "Çözüm:",
    painOverwhelmedFix:
      "Zaten ne kullandığınızı soruyoruz; AI Implementation Blueprint’iniz o stack etrafında model‑özel prompt kütüphanesi ve yayılım planı içeriyor.",
    painCostTitle: "Maliyetten şüpheli",
    painCostDesc:
      "10.000+ TL/aylık kurumsal yazılımlar için bütçeniz yok. Uygun fiyatlı ama işe yarayan bir şey istiyorsunuz.",
    painCostFixLabel: "Çözüm:",
    painCostFix:
      "Faz 1’deki araçların çoğu aylık $0–$49 aralığında. Blueprint’iniz fiyatları baştan gösterir ve bütçenize uyan hızlı kazanımları öne çıkarır.",
    painTimeTitle: "Öğrenecek zaman yok",
    painTimeDesc:
      "Prompt engineering öğrenmek için 10 saatiniz yok. Sadece daha iyi e‑postalar, reklamlar ve sistemler istiyorsunuz.",
    painTimeFixLabel: "Çözüm:",
    painTimeFix:
      "Blueprint’iniz, işletmenize göre özelleştirilmiş 15–30 kopyala‑yapıştır prompt içerir; bugün kullanmaya başlayabilirsiniz.",

    founderTitle: "Kurucunun hikâyesi",
    founderQuote:
      "10 yıl boyunca Uber için araç kullandım ve binlerce küçük işletme sahibiyle konuştum. Hepsinin en çok istediği şey daha fazla teknoloji değil, daha fazla zamandı. AI Implementation Blueprint’i bu yüzden geliştirdim.",
    founderName: "Ali Erdem",
    founderRole: "Kurucu, AI Workshop LLC",
    founderCta:
      "İşletmeniz hakkında Ali ile sohbet edin",

    pricingTitle:
      "Küçük işletmeler için basit fiyatlandırma",
    pricingPriceLabel: "$149 tek seferlik",
    pricingBadge: "AI Implementation Blueprint",
    pricingItem1:
      "Kişiselleştirilmiş 3–5 aşamalı AI uygulama yol haritası",
    pricingItem2:
      "Stack’inize uyarlanmış kurulum adımları ve fiyatlarla araç listesi",
    pricingItem3:
      "Haftalık görevler ve basit KPI’larla 30 günlük aksiyon planı",
    pricingItem4:
      "Grok, ChatGPT, Claude veya Gemini için 15–30 adet özel prompt kütüphanesi",
    pricingItem5:
      "Risk analizi, koruma önlemleri ve basit AI yönetişim checklist’i",
    pricingItem6:
      "60 günlük para iade garantisi",
    pricingSecure: "Stripe ile güvenli ödeme",

    testimonialsTitle:
      "Diğer işletme sahipleri ne diyor",
    testimonialsSubtitle:
      "Beta programımızdan erken erişim sonuçları.",

    finalCtaTitle:
      "AI Implementation Blueprint’inizi almaya hazır mısınız?",
    finalCtaButton:
      "Ücretsiz denetimi başlatın, Blueprint’inizi görün",
    finalCtaSubtitle:
      "Yaklaşık 5 dakika • Ödeme yapmadan önce AI Implementation Blueprint önizlemesini görün",

    // Meta
    metaTitle: "İşletmeniz için AI Blueprint | AI Workshop",

    // Social proof
    socialProofText: "127'den fazla KOBİ sahibi tarafından güvenildi",

    // Hero bullets + extras
    heroBullet1: "İşletmenize özel — genel bir kontrol listesi değil",
    heroBullet2: "Bütçenize, platformunuza ve sorunlarınıza göre eşleştirilmiş araçlar",
    heroBullet3: "60 günlük para iade garantisi",
    heroPrimaryCtaPrice: "Blueprint'imi Al · {price}",
    heroSecureLabel: "Stripe ile güvenli ödeme",

    // How it works
    howBadge: "Nasıl Çalışır",
    howSubtitle: "AI Implementation Blueprint'inize üç basit adımda ulaşın.",
    howStep1Time: "Adım 1",
    howStep2Time: "Adım 2",
    howStep3Time: "Adım 3",

    // Industries strip
    industriesStripLabel: "Kapsadığımız sektörler",
    industryEcom: "E-ticaret",
    industryRestaurants: "Restoranlar",
    industryHealthcare: "Sağlık",
    industryRealEstate: "Gayrimenkul",
    industrySaaS: "SaaS",
    industryProfServices: "Profesyonel Hizmetler",

    // Stats
    statsToolsLabel: "Değerlendirilen AI Araçları",
    statsIndustriesLabel: "Kapsanan Sektörler",

    // Features "What You Get"
    featuredMostValued: "En Değerli",
    wyg1Title: "12 Haftalık Uygulama Yol Haritası",
    wyg1Desc: "Ekip büyüklüğünüze ve bütçenize göre uyarlanmış aşamalı dağıtım planı.",
    wyg1Stat: "Ortalama hafta 2'de 8 saat tasarruf",
    wyg2Title: "{{toolCount}}+ Değerlendirilen AI Aracı",
    wyg2Desc: "Sektörünüze özel kurulum adımları, fiyatlandırma ve risk puanlarıyla seçilmiş araçlar.",
    wyg2Stat: "Size özel en iyi 6 araç",
    wyg3Title: "Özel Prompt Kütüphanesi",
    wyg3Desc: "AI asistanınız için hazır 15–30 prompt (Grok, ChatGPT, Claude veya Gemini).",
    wyg3Stat: "Her AI asistanıyla çalışır",
    wyg4Title: "ROI Projeksiyonları",
    wyg4Desc: "Gelirinize ve sektör kıyaslamalarına göre 1. yıl ROI tahmini ve geri ödeme süresi.",
    wyg4Stat: "Ortalama 1. yıl etkisi $24K",
    wyg5Title: "İlk 48 Saat Aksiyon Planı",
    wyg5Desc: "Bugün, yarın ve bu hafta ne yapacağınızı tam olarak öğrenin — tahmin yok.",
    wyg5Stat: "2–7 günde ilk sonuç",
    wyg6Title: "60 Günlük Para İade Garantisi",
    wyg6Desc: "Blueprint'inizden en az bir zaman kazandıran otomasyon bulamazsanız para iadesi yapıyoruz.",
    wyg6Stat: "Sıfır risk",

    // Testimonials (removed — awaiting real customer results)

    // Blueprint mockup tool names/badges
    mockupBlueprintLabel: "AI Implementation Blueprint",
    mockupTool1Name: "Klaviyo AI",
    mockupTool1Badge: "E-posta · $45/ay",
    mockupTool2Name: "Tidio Chat",
    mockupTool2Badge: "Destek · Ücretsiz",
    mockupTool3Name: "Triple Whale",
    mockupTool3Badge: "Analitik · $129/ay",

    // Pricing
    pricingSubtitle: "Abonelik yok. Ek satış yok. Bir blueprint, her şey dahil.",
    pricingMostPopular: "En Popüler",
    pricingSavings: "tek seferlik · abonelik yok",
    pricingCta: "AI Blueprint'imi Al",
    pricingIncludedLabel: "Neler dahil:",
    pricingInc1Title: "3 Aşamalı AI Yol Haritası",
    pricingInc1Sub: "Sektörünüze ve ekibinize özel",
    pricingInc2Title: "{{toolCount}}+ Araç Veritabanı",
    pricingInc2Sub: "Sizin için sıralanmış en iyi 6 araç",
    pricingInc3Title: "Prompt Kütüphanesi",
    pricingInc3Sub: "AI asistanınız için 15–30 prompt",
    pricingInc4Title: "ROI Projeksiyonları",
    pricingInc4Sub: "1. yıl etkisi ve geri ödeme süresi",
    pricingInc5Title: "Risk Analizi",
    pricingInc5Sub: "Güvenlik önlemleri ve yönetişim listesi",
    pricingInc6Title: "İndirilebilir PDF",
    pricingInc6Sub: "Tam rapor + aksiyon planı",

    // Comparison table
    cmpTitle: "Nasıl karşılaştırıyoruz",
    cmpSubtitle: "AI Workshop vs. danışman tutmak veya kendi başınıza yapmak",
    cmpColFeature: "Özellik",
    cmpColConsultant: "Danışman",
    cmpColDiy: "Kendi Başına",
    cmpRow1: "İşletmenize özel",
    cmpRow2: "Fiyatlandırmalı araç önerileri",
    cmpRow3: "Adım adım kurulum kılavuzları",
    cmpRow4: "Özel prompt kütüphanesi",
    cmpRow5: "ROI projeksiyonları",
    cmpRow6: "Para iade garantisi",
    cmpRowCost: "Toplam maliyet",
    cmpSometimes: "Bazen",
    cmpDiyHours: "∞ saat",
    cmpCta: "{price} Blueprint'imi Al",

    // Live activity ticker (removed — no fabricated activity)
    trendEmerging: "Yükselen",
    trendGrowing: "Büyüyen",

    // New arrivals marquee
    newArrivalsBadge: "Araç Veritabanı Güncellendi",
    newArrivalsTitle: "Bu hafta eklenen yeni AI araçları",
    newArrivalsSubtitle: "Ekibimiz her hafta yeni araçları inceler ve ekler — Blueprint'iniz her zaman en güncel verileri kullanır.",
    newArrivalsAdded: "Eklendi",
    newArrivalsRecent: "Yakın zamanda eklendi",
    newArrivalsCtaLink: "En yeni araçlarla Blueprint'inizi alın",

    // Blueprint mockup
    mockupStoreLabel: "İşletme Adınız",
    mockupReportReady: "Rapor Hazır",
    mockupYear1Roi: "1. Yıl ROI",
    mockupSavedWeek: "Tasarruf / Hafta",
    mockupRecommended: "Önerilen",
    mockupTop3Tools: "Sizin İçin En İyi 3 Araç",
    mockupMatch: "uyum",
    mockupImplProgress: "Blueprint Uygulama İlerlemesi",
    mockupProgressDone: "6'dan 1'i tamamlandı",
    mockup90DayRoadmap: "12 Haftalık Yol Haritası",
    mockupPhase1: "Aşama 1\nTemel\nHft. 1–4",
    mockupPhase2: "Aşama 2\nBüyüme\nHft. 5–8",
    mockupPhase3: "Aşama 3\nÖlçekleme\nHft. 9–12",
    mockupGeneratedBadge: "AI ile Oluşturuldu",
    mockupToolsEvaluated: "araç değerlendirildi",

    // Final CTA extras
    finalCtaEngineLive: "Motor aktif · {count} araç",
    finalCtaCheck1: "Denetim için kredi kartı gerekmez",
    finalCtaCheck2: "Tam Blueprint yalnızca {price}",
    finalCtaCheck3: "60 günlük para iade garantisi",

    // Subscribe extras
    subscribeEmailLabel: "E-posta adresi",
    subscribeErrorEmail: "Lütfen geçerli bir e-posta adresi girin.",
    subscribeError: "Bir şeyler ters gitti. Lütfen tekrar deneyin.",
    subscribeSuccess: "Abone oldunuz! Gelen kutunuzu kontrol edin.",
  },

  hero: {
    badge: "AI Benimseme Motoru v3.0",
    title: "İşletmenizi AI ile Dönüştürün",
    subtitle:
      "50.000'den fazla KOBİ uygulamasına dayanan kişiselleştirilmiş AI uygulama planı alın. Ücretsiz denetim, eyleme dönüştürülebilir içgörüler.",
    cta: "Ücretsiz AI Denetimini Başlat",
    ctaSecondary: "Nasıl Çalıştığını Gör",
    trusted:
      "50.000'den fazla işletme tarafından güveniliyor",
  },

  // Audit multi‑step
  audit: {
    required: "(zorunlu)",

    topBadge: "AI Uygulama Denetimi · Adım {{step}} / 4",
    topTitle:
      "AI Implementation Blueprint’inizi oluşturun",
    topSubtitle:
      "Birkaç odaklı soruyu cevaplayın; {{industry}} için 3 aşamalı AI yol haritası, araç listesi, prompt paketi ve ROI projeksiyonları çıkaralım.",
    estimateMinutes: "2–3 dakika",
    industryFallback: "sektörünüz",

    step1Title: "İşletmeniz",
    step1Subtitle:
      "Kim olduğunuzu ve hangi alanda faaliyet gösterdiğinizi anlayalım ki doğru karşılaştırmaları kullanalım.",
    fieldBusinessName: "İşletme adı",
    placeholderBusinessName:
      "Örn: Coastal Home Supply",
    helperBusinessName:
      "Rapor başlığı ve bağlamı için kullanılır.",
    fieldIndustry: "Sektör",
    placeholderIndustry:
      "Sektörünüzü seçin",
    helperIndustry:
      "Benzer KOBİ’lerle karşılaştırma yapacağız.",
    industryRetail: "Perakende & E‑ticaret",
    industryHospitality:
      "Yiyecek‑içecek & Restoran",
    industryProf:
      "Profesyonel Hizmetler",
    industryTech: "Teknoloji & SaaS",
    industryFinance: "Finans & Bankacılık",
    industryManufacturing: "Üretim",
    industryRealEstate: "Gayrimenkul",
    industryEducation: "Eğitim",
    industryOther: "Diğer",

    step2Title:
      "Büyüklük, bütçe ve ne sattığınız",
    step2Subtitle:
      "Bu bilgiler, yol haritasını ekibiniz ve bütçeniz için gerçekçi hale getirmemizi sağlar.",
    step2Group1Title: "Büyüklük ve bütçe",
    fieldBusinessSize: "İşletme büyüklüğü",
    placeholderBusinessSize:
      "Büyüklük seçin",
    helperBusinessSize:
      "Ekibinizde kaç kişi var?",
    sizeSolo: "Solo (1 kişi)",
    sizeSmall: "Küçük (2–10 çalışan)",
    sizeMedium: "Orta (11–50 çalışan)",
    sizeLarge: "Büyük (51+ çalışan)",

    fieldBudget: "AI bütçesi",
    placeholderBudget: "Aylık bütçe",
    helperBudget:
      "AI araçları ve hizmetleri için tahmini aylık bütçe.",
    budgetUnder200: "200$ altında",
    budgetUnder500: "200$ – 500$",
    budgetUnder2k: "500$ – 2.000$",
    budget2k5k: "2.000$ – 5.000$",
    budget5k10k: "5.000$ – 10.000$",
    budget10k25k: "10.000$ – 25.000$",
    budget25kPlus: "25.000$+",

    step2Group2Title:
      "Ne satıyorsunuz, hangi AI’ı kullanıyorsunuz?",
    fieldBusinessType: "İş modeli",
    placeholderBusinessType:
      "İş modelinizi seçin",
    helperBusinessType:
      "Hangi büyüme kaldıraçlarını önceliklendireceğimizi belirlememize yardımcı olur.",
    businessTypeEcom:
      "E‑ticaret / Online mağaza",
    businessTypeServices:
      "Hizmet / Ajans / Profesyonel",
    businessTypeBoth:
      "Hem ürün hem hizmet",

    fieldEcommercePlatform:
      "Ana e‑ticaret platformu",
    placeholderEcommercePlatform:
      "Platform seçin",
    helperEcommercePlatform:
      "Blueprint’inizi bu platforma göre uyarlayacağız.",
    platformCustom: "Özel / Diğer",

    fieldAiAssistant:
      "Kullandığınız AI asistanı",
    placeholderAiAssistant:
      "En çok hangi AI asistanını kullanıyorsunuz?",
    helperAiAssistant:
      "Prompt kütüphanenizi zaten kullandığınız modele göre ayarlayacağız.",
    aiAssistantNone:
      "Henüz düzenli olarak kullanmıyorum",

    step3Title:
      "Problemler, hedefler ve mevcut araçlar",
    step3Subtitle:
      "Nerede tıkandığınızı ve sizin için “kazanç”ın ne anlama geldiğini paylaşın ki en yüksek etkiyi getiren hamleleri öne alalım.",
    fieldProblems:
      "En büyük problemleriniz neler?",
    placeholderProblems:
      "Örn.: \"Her Cuma 4–5 saatimi QuickBooks’tan Excel’e manuel fatura kopyalamak ve geciken ödemeler için tek tek e-posta göndermekle geçiriyorum. Bu çeyrek 3 müşterim 90+ gün gecikti.\"\n\nNe kadar spesifik olursanız, blueprint’iniz o kadar size özel hissettiriyor.",
    helperProblems:
      "Tam görevi, ne kadar sürdüğünü ve size ne kadara mal olduğunu yazın. Detay = daha kişisel blueprint.",
    fieldGoals: "Hedefleriniz neler?",
    placeholderGoals:
      "Örn: Geliri artırmak, kurucunun zamanını boşaltmak, müşteri deneyimini iyileştirmek…",
    fieldCurrentTools:
      "Halihazırda hangi araçları kullanıyorsunuz?",
    placeholderCurrentTools:
      "Shopify, Toast, HubSpot, Notion, ClickUp vb. araçları listeleyin.",
    fieldEmail:
      "Blueprint’i hangi e‑posta adresine gönderelim?",
    placeholderEmail: "siz@isletmeniz.com",
    helperEmail:
      "AI Implementation Blueprint’inizi ve önizleme bağlantısını buraya göndereceğiz.",

    howWeUseTitle:
      "Cevaplarınızı nasıl kullanıyoruz?",
    howWeUseIntro:
      "Cevaplarınız asla satılmaz. Yalnızca AI Implementation Blueprint’inizi iyileştirmek için kullanıyoruz:",
    howWeUseItem1:
      "Size benzer büyüklükteki işletmelerle karşılaştırma yapmak.",
    howWeUseItem2:
      "Araçlarınıza, bütçenize ve ekibinize uyan hamleleri önceliklendirmek.",
    howWeUseItem3:
      "İşletmenize benzeyen prompt’lar ve örnekler üretmek.",

    priorityLabel:
      "Öncelikli destek: Blueprint’inizi hızlandırırız ve ek sorumuz olursa size e‑posta göndeririz.",

    step4Title:
      "Cevaplarınızı gözden geçirin",
    step4Subtitle:
      "AI Implementation Blueprint’inizi oluşturmadan önce her şeyin doğru olduğundan emin olun.",
    reviewBusiness: "İşletmeniz",
    reviewSize: "Ekip büyüklüğü:",
    reviewTypeBudget:
      "İş modeli ve bütçe",
    reviewBudget: "AI bütçesi:",
    reviewAiAssistant: "AI asistanı:",
    reviewContact: "İletişim ve teslimat",
    reviewPriority:
      "Öncelikli destek:",
    reviewProblems:
      "Başlıca problemleriniz",
    reviewGoals: "Hedefleriniz",
    reviewTools:
      "Mevcut araçlarınız",
    notSet: "Belirtilmedi",
    yes: "Evet",
    no: "Hayır",

    suggestedLabel: "Önerilen",
    suggestedTools: "araç",
    suggestedGeneric: "öğe",
    clearChips: "Temizle",
    loadingChips:
      "Akıllı öneriler yükleniyor…",

    buttonCancel: "İptal",
    buttonBack: "Geri",
    buttonContinue: "Devam",
    buttonGenerate:
      "Blueprint’imi Oluştur",
    buttonGenerating:
      "Blueprint’iniz oluşturuluyor…",

    errorBusinessName:
      "İşletme adı en az 2 karakter olmalı",
    errorIndustry:
      "Lütfen sektörünüzü seçin",
    errorBusinessSize:
      "Lütfen işletme büyüklüğünü seçin",
    errorBudget:
      "Lütfen bütçe aralığını seçin",
    errorBusinessType:
      "Lütfen iş modelinizi seçin",
    errorEcommercePlatform:
      "Lütfen ana satış platformunuzu seçin",
    errorAiAssistant:
      "Lütfen ağırlıklı olarak kullandığınız AI asistanını seçin",
    errorProblems:
      "Problemlerinizi biraz daha ayrıntılı yazın (en az 10 karakter)",
    errorEmail:
      "Geçerli bir e‑posta adresi girin",
    errorFillAll:
      "Lütfen tüm zorunlu alanları doğru şekilde doldurun",

    submitSuccess:
      "Başarılı! AI Implementation Blueprint’iniz hazır — e‑postanızı kontrol edin.",
    submitGenericError:
      "Rapor oluşturulamadı. Lütfen tekrar deneyin.",
  },

  form: {
    title: "Ücretsiz AI Denetimi",
    subtitle:
      "Kişiselleştirilmiş AI uygulama planınızı dakikalar içinde alın",
    businessName: "İşletme Adı",
    businessNamePlaceholder:
      "İşletme adınızı girin",
    industry: "Sektör",
    industryPlaceholder:
      "Sektörünüzü seçin",
    businessSize: "İşletme Büyüklüğü",
    businessSizePlaceholder:
      "Ekip büyüklüğünü seçin",
    budget: "AI Uygulama Bütçesi",
    budgetPlaceholder:
      "Bütçe aralığını seçin",
    problems: "Mevcut Zorluklar",
    problemsPlaceholder:
      "Ana iş zorluklarınızı açıklayın...",
    goals: "Hedefler",
    goalsPlaceholder:
      "AI ile ne başarmak istiyorsunuz?",
    currentTools: "Mevcut Araçlar",
    currentToolsPlaceholder:
      "Şu anda kullandığınız araçları listeleyin (örn: Shopify, QuickBooks)",
    email: "E-posta Adresi",
    emailPlaceholder:
      "E-postanızı girin",
    submit: "AI Planımı Oluştur",
    submitting:
      "İşletmeniz analiz ediliyor...",
  },

  industries: {
    retail: "Perakende / E-ticaret",
    hospitality: "Restoran / Otel",
    healthcare: "Sağlık / Medikal",
    professional:
      "Profesyonel Hizmetler",
    technology: "Teknoloji / SaaS",
    finance: "Finans / Bankacılık",
    manufacturing: "Üretim",
    real_estate: "Gayrimenkul",
    education: "Eğitim",
    other: "Diğer",
  },

  sizes: {
    solo: "Solo (Sadece ben)",
    small: "2-5 çalışan",
    medium: "6-20 çalışan",
    large: "21-50 çalışan",
    enterprise: "50+ çalışan",
  },

  budgets: {
    under5k: "5.000$'ın altında",
    "5k-10k": "5.000$ - 10.000$",
    "10k-25k": "10.000$ - 25.000$",
    "25k-50k": "25.000$ - 50.000$",
    "50k+": "50.000$+",
  },

  preview: {
    title: "AI Uygulama Önizlemeniz",
    previewMode: "Önizleme Modu",
    unlockFull: "Tam Raporu Aç",
    price: "$149",
    executiveSummary: "Yönetici Özeti",
    keyOpportunities: "Temel Fırsatlar",
    viewedFor: "Görüntüleme süresi",
    minutes: "dakika",
    includes: "Tam rapor içerir",
    includesList: [
      "ROI ile 4 Öncelikli Eylem Adımı",
      "AI Olgunluk Endeksi ve Hız Puanı",
      "18.000+ kayıttan Sektör Karşılaştırmaları",
      "Özel Blueprint Parçacıkları",
      "SEO Optimizasyon Kiti",
      "PDF İndirme ve E-posta Teslimi",
    ],

    errorNotFound: "Rapor bulunamadı",
    errorInvalid:
      "Sunucudan geçersiz rapor verisi alındı",
    errorGeneric:
      "Önizleme yüklenemedi. Lütfen tekrar deneyin.",
    errorTitle: "Önizleme Yüklenirken Hata",
    businessFallback: "İşletme",
    metaDescription:
      "{{business}} için kişiselleştirilmiş AI Implementation Blueprint’inizi önizleyin.",
    moneyBackBadge:
      "🛡️ 60 günlük para iade garantisi",
    moneyBackLong:
      "🛡️ 60 günlük para iade garantisi — playbook’ları ve checklist’i deneyin; yardımcı olmazsa paranızı iade ederiz. Soru yok.",
    moneyBackShort:
      "🛡️ 60 günlük garanti — koşulsuz iade",

    loading:
      "AI Implementation Blueprint’iniz hazırlanıyor...",

    assistantFallback: "kullandığınız AI asistanı",

    bizDescShopify:
      "Shopify mağaza sahipleri",
    bizDescWoo: "online mağazalar",
    bizDescBigcommerce: "e‑ticaret markaları",
    bizDescSquarespace:
      "Squarespace mağazaları",
    bizDescWix: "Wix mağazaları",
    bizDescEcom: "e‑ticaret kurucuları",
    bizDescServices:
      "hizmet işletmeleri",
    bizDescIndustryOwners:
      "{{industry}} sahipleri",
    bizDescGeneric:
      "işletme sahipleri",

    bonusHealthcareTitle:
      "HIPAA Uyum Checklist’i",
    bonusHealthcareDesc:
      "Hasta verilerini güvende tutan önceden test edilmiş araçlar ve iş akışları.",
    bonusFinanceTitle:
      "SOC2 & PCI‑DSS Rehberi",
    bonusFinanceDesc:
      "Finansal hizmetler için denetime hazır yapılandırmalar.",
    bonusEducationTitle:
      "FERPA Uyumlu AI Araçları",
    bonusEducationDesc:
      "Öğrenci verilerini korurken idari işleri otomatikleştirin.",
    bonusGenericTitleFallback:
      "Sektörünüz için AI Starter Pack",
    bonusGenericDesc:
      "{{industry}} için özel hazırlanmış prompt’lar ve şablonlar.",
    bonusYourBusiness: "işletmeniz",

    heroHeadline:
      "{{descriptor}} için AI Implementation Blueprint’iniz",
    forBusiness: "{{business}} için",
    yourBusiness: "İşletmeniz",
    yourIndustry: "sektörünüz",

    heroPillRoadmap:
      "✅ {{industry}} için uyarlanmış 3 aşamalı AI yol haritası",
    heroPillPlaybooks:
      "✅ Hazır AI Playbook’lar (plug‑and‑play iş akışları)",
    heroPillChecklist:
      "✅ Ekibiniz için 90 günlük uygulama checklist’i",
    heroPillPrompts:
      "✅ {{assistant}} için özel prompt kitaplığı",

    blueprintSummary:
      "📄 Blueprint’iniz: {{pages}} sayfa, {{tools}} araç, {{phases}} aşama.",
    blueprintHiddenGems:
      "(içinde {{count}} adet “gizli mücevher” araç)",

    includesPlaybooks:
      "{{count}} adet AI Playbook içerir",
    includesChecklist:
      "ve ekibinizin her hafta ne yapacağını bilmesi için 90 günlük checklist.",

    projectedY1:
      "1. yıl için beklenen kazanç: {{amount}}",
    consultantValue:
      "Bir AI danışmanlık seansı saati $300–$500 tutar. Bu blueprint, 4–6 saatlik danışmanlığın kapsayacağı her şeyi içerir — {{price}}, tek seferlik ödeme.",
    paysForItself:
      "Eğer bu yıl size yalnızca {{smallWin}} ek kâr veya tasarruf kazandırsa bile, kendini {{multiplier}}× katı geri öder.",

    cohortProof:
      "50k+ KOBİ kaydı ve McKinsey / Gartner / ICONIQ 2025 araştırmalarıyla desteklenir",

    insideTimelineTitle:
      "12 Haftalık AI Uygulama Zaman Çizelgeniz",
    insideTimelineDesc:
      "Yanıtlarınıza göre belirlenmiş hedefler, teslimatlar ve kilometre taşlarıyla hafta hafta plan — böylece neyi önce, neyi sonra yapacağınızı bilirsiniz.",
    insidePlaybooksTitle:
      "Sizin İçin Hazırlanmış AI Playbook’lar",
    insidePlaybooksDesc:
      "En büyük darboğazlarınız için çalıştır‑git iş akışları — örneğin {{p1}} ve {{p2}}. Her playbook araçları, adımları, sorumluları ve beklenen sonucu net şekilde listeler.",
    insidePromptsTitle:
      "Özel Prompt Kütüphanesi",
    insidePromptsDesc:
      "{{assistant}} için, başlıca problemleriniz ve stack’inizdeki araçlara göre yazılmış kopyala‑yapıştır prompt’lar — “bana bir pazarlama planı yaz” tarzı genel şeyler değil.",
    insideChecklistTitle:
      "90 Günlük Uygulama Checklist’i",
    insideChecklistDesc:
      "Yönetim, operasyon ve pazarlama için hafta hafta görevler; böylece ekibiniz planı AI uzmanı olmadan da uygulayabilir — blueprint PDF’de kalmaz.",

    lockedRoadmapTitleBusiness:
      "{{businessName}} için AI Implementation Blueprint hazır",
    lockedRoadmapTitleGeneric:
      "Tam AI Implementation Blueprint’iniz hazır",
    lockedRoadmapDesc:
      "{{business}} için 90 günlük eksiksiz bir AI yol haritası oluşturduk — plug‑and‑play AI Playbook’lar ve 90 günlük checklist ile siz ve ekibiniz her hafta ne yapacağınızı biliyorsunuz.",
    lockedRoadmapValue:
      "Tek seferlik satın alma — abonelik yok. Blueprint'inizi {{price}} karşılığında alın ve sonsuza kadar saklayın.",
    roadmapCardTitle:
      "AI Uygulama Yol Haritanız",
    phase1Title: "Faz 1: {{name}}",
    phase1Focus: "Odak: {{focus}}",
    phase2Title: "Faz 2: Ölçekle ve Otomatize Et",
    phase2Desc:
      "AI’ı farklı departmanlara entegre edin.",
    phase3Title: "Faz 3: Optimize Et ve Yenilik Yarat",
    phase3Desc:
      "İnce ayar yapın ve yeni fırsatları keşfedin.",

    lockedPromptsTitleBusiness:
      "{{businessName}} için Özel Prompt Kütüphanesi",
    lockedPromptsTitleGeneric:
      "Özel Prompt Kütüphaneniz",
    lockedPromptsDesc:
      "En büyük problemleriniz ve playbook’larınızdaki araçlar için hazır kopyala‑yapıştır prompt’lar yazdık — bugün {{assistant}} içinde kullanmaya hazırsınız.",
    lockedPromptsValue:
      "💡 Prompt paketi tek başına ajansların yüzlerce dolar aldığı bir ürün.",
    promptsCardTitle:
      "Özel Prompt Kütüphaneniz",
    promptFor: "Prompt konusu: {{problem}}",

    bonusesTitle:
      "Özel Üyelik Bonusları (Yakında)",
    bonusesSubtitle:
      "Bu bonusları sizin için inşa ediyoruz. Lansman müşterisi olarak hazır olduklarında ömür boyu erişiminiz olacak.",
    bonusBootcampTitle:
      "30 Günlük Uygulama Bootcamp’i",
    bonusBootcampDesc:
      "Kurulumdan ilk sonuca kadar her adımda sizi yönlendiren günlük e‑postalar.",
    bonusCommunityTitle:
      "Özel Topluluk Erişimi",
    bonusCommunityDesc:
      "AI uygulayan diğer kurucularla bağlantı kurun — soru sorun, başarılarınızı paylaşın.",
    bonusCoachTitle:
      "AI Koç Chatbot",
    bonusCoachDesc:
      "Uygulama ile ilgili sık sorulan sorulara 7/24 cevap alın.",
    bonusDiscountsTitle:
      "Özel Araç İndirimleri",
    bonusDiscountsDesc:
      "Blueprint’inizde yer alan önde gelen AI ve otomasyon araçlarında tasarruf edin.",
    bonusRefreshTitle:
      "Üç Aylık Blueprint Güncellemesi",
    bonusRefreshDesc:
      "Planınız her 3 ayda bir yeni benchmark’lar ve araçlarla güncellenebilir.",

    researchBadge:
      "📊 Sahip olduğumuz KOBİ verileri + dış araştırmalar (McKinsey, Gartner, ICONIQ 2025) üzerine kurulu",
    promoTitle:
      "Promosyon kodunuz var mı?",
    promoLabel: "Promosyon kodu",
    promoPlaceholder: "Kodu girin",
    promoContinue: "Devam et",

    testimonialsTitle:
      "İşletme sahipleri ne diyor?",
    testimonialsSubtitle:
      "Beta AI Implementation Blueprint programımızdan alınan erken sonuçlar.",
    // testimonials removed — awaiting real customer results

    faqTitle:
      "Sıkça Sorulan Sorular",
    faq1Q:
      "Sonuçları ne kadar hızlı görürüm?",
    faq1A:
      "Çoğu işletme, önerilen ilk aracı bir hafta içinde kurup çalıştırabilir ve ilk ay içinde daha az manuel iş ya da daha hızlı müşteri yanıtları gibi iyileşmeler görür. Playbook’lar ve checklist hızlı kazanımlar için tasarlandı, aylar süren planlama için değil.",
    faq2Q:
      "Ekibim bunu gerçekten kullanır mı?",
    faq2A:
      "Evet — belirsiz bir strateji sunumu yerine somut AI Playbook’lar ve 90 günlük checklist alırsınız. Bu da net sorumlular, haftalık görevler ve AI uzmanı olmadan izleyebilecekleri basit adımlar demektir.",
    faq3Q:
      "Ya AI benim işletmem için işe yaramazsa?",
    faq3A:
      "Önerilerimiz binlerce işletmeden gelen kalıplara dayanıyor, ancak her işletmenin benzersiz olduğunu biliyoruz. Bu yüzden 60 günlük garanti veriyoruz: Blueprint size ilerleme sağlamazsa, revize eder veya ücretinizi iade ederiz.",
    faq4Q:
      "Bu, genel bir AI rehberinden nasıl farklı?",
    faq4A:
      "Bu tek tip bir checklist değil. Her araç, prompt, playbook ve faz; sektörünüz, büyüklüğünüz ve paylaştığınız özel zorluklara göre seçilir. Sanki bir danışman yalnızca sizin için plan hazırlamış gibi — danışmanlık ücreti olmadan.",

    exitTitle:
      "Henüz satın almaya hazır değil misiniz?",
    exitSubtitle:
      "En büyük AI fırsatlarınızdan 3’ünün ücretsiz özetini e‑postanıza gönderelim.",
    exitEmailPlaceholder:
      "E‑posta adresinizi girin",
    exitButton: "İpuçlarını Gönder",
    exitFinePrint:
      "Gizliliğinize saygı duyuyoruz. İstediğiniz zaman ayrılabilirsiniz.",

    unlockBusinessBlueprint:
      "{{businessName}} için Blueprint’i aç",
    unlockMyBlueprint:
      "AI Blueprint’imi aç",
    unlockBusinessPrompts:
      "{{businessName}} için prompt’ları aç",
    unlockPromptPack:
      "Prompt paketini aç",

    waitlistErrorEmail:
      "Lütfen e‑posta adresinizi girin",
    waitlistThanks:
      "Teşekkürler! Size ücretsiz 3 ipucundan oluşan bir özet göndereceğiz.",
    waitlistErrorGeneric:
      "Bir hata oluştu. Lütfen tekrar deneyin.",
    waitlistErrorNetwork:
      "Ağ hatası. Lütfen tekrar deneyin.",
  },

  payment: {
    title: "Tam AI Raporunuzu Açın",
    subtitle:
      "Kişiselleştirilmiş AI uygulama planınıza tam erişim alın",
    promoCode: "Promosyon kodunuz var mı?",
    promoPlaceholder: "Kodu girin",
    apply: "Uygula",
    payNow: "Şimdi Öde",
    securePayment: "Stripe ile güvenli ödeme",
    moneyBack:
      "30 günlük para iade garantisi",
  },

  // UPDATED report block with full tab + playbooks/checklist/governance/guide
  report: {
    title: "AI Uygulama Planınız",
    preparedFor: "Hazırlanan",
    generatedOn: "Oluşturulma tarihi",
    downloadPdf: "PDF İndir",
    emailReport: "E-posta ile Gönder",
    tabs: {
      maturity: "Olgunluk",
      data: "Veri",
      actions: "Aksiyonlar",
      tools: "Araçlar",
      roi: "ROI",
      seo: "SEO Kiti",
      services: "Hizmetler",
    },
    maturityIndex: "AI Olgunluk Endeksi",
    maturityExplanation: "AI Olgunluğunuz",
    timeToValue:
      "Değere Beklenen Süre",
    weeks: "hafta",
    scoreBreakdown: "Puan Dağılımı",
    riskFlags:
      "Kişiselleştirilmiş Risk Bayrakları",
    actionSteps:
      "Öncelikli Eylem Adımları",
    impactScore: "Etki Puanı",
    expectedRoi: "Beklenen ROI",
    setupTime: "Kurulum Süresi",
    tool: "Araç",
    addressesProblem:
      "Probleminizi Çözüyor",
    blueprint: "Özel Blueprint",
    toolSequence: "Araç Sıralaması",
    successKpis:
      "Başarı KPI'ları",
    roiProjections:
      "ROI Projeksiyonları",
    monthlySavings:
      "Aylık Tasarruf",
    annualImpact: "Yıllık Etki",
    paybackPeriod:
      "Geri Ödeme Süresi",
    hoursSaved:
      "Haftalık Kazanılan Saat",
    industryBenchmarks:
      "Sektör Karşılaştırmaları",
    aiRecommendation:
      "AI Destekli Öneri",
    opportunityIdentified:
      "Tespit Edilen Fırsat",
    expectedLift: "Beklenen Artış",

    // NEW: toasts, share, actions, meta, full tabs
    toasts: {
      copyError:
        "Kopyalama başarısız. Metni seçip manuel olarak kopyalayabilirsiniz.",
      shareSuccess:
        "Paylaşım linki panoya kopyalandı!",
      shareError:
        "Paylaşım metni kopyalanamadı. Lütfen manuel kopyalayın.",
      downloadUnexpected:
        "İndirme başarısız: Sunucu beklenmeyen bir içerik döndürdü.",
      downloadSuccess:
        "AI Implementation Blueprint indirildi!",
      downloadError:
        "İndirme başarısız oldu. Bunun yerine yazdırmayı deneyin.",
    },
    share: {
      text:
        "AI Workshop LLC’den AI Implementation Blueprint’imi yeni aldım — bir göz at: {{url}}",
    },
    actions: {
      backToPreview: "Önizlemeye geri dön",
      print: "Yazdır",
      share: "Paylaş",
      downloadPreparing: "İndirme hazırlanıyor…",
      downloadTitle:
        "AI Implementation Blueprint’i indir",
      downloadCta: "Blueprint’i PDF olarak indir",
    },
    pageTitle: "AI Implementation Blueprint",
    pageTitleWithName:
      "AI Implementation Blueprint | {{businessName}}",
    metaDescription:
      "{{industry}} sektöründeki {{businessName}} için AI Implementation Blueprint’iniz.",
    fullTabs: {
      tools: "Araçlar",
      diagnosis: "Teşhis",
      prompts: "Prompts",
      risks: "Riskler",
      market: "Pazar",
      departments: "Departmanlar",
      financials: "Finans",
      competitive: "Rekabet",
      governance: "Yönetişim",
      guide: "Rehber",
    },
    first48: {
      title: "İlk 48 Saatiniz",
      subtitle: "Boş söz yok. Belirli adımlar, belirli araçlar. Bugün başla.",
      badge: "Eylem Planı",
      day1: "Gün 1 — Bugün Yap",
      day2: "Gün 2 — Yarın Yap",
    },
    benchmark: {
      title: "Siz ve Sektörünüz",
      badge: "İlk Hamle Fırsatı Açık",
      firstMoverDesc: "{industry} işletmelerinin %{pct}'i henüz yapay zekayı benimsemedi — piyasa yetişmeden önce onları geçmek için bu sizin fırsatınız.",
      adoptedLabel: "sektörünüzde\nzaten AI kullanıyor",
      notAdoptedLabel: "henüz\nbaşlamadı",
      liftLabel: "üst araçtan\nortalama artış",
      sampleNote: "{toolName}'ı uygulayan {count} gerçek işletmeye dayanmaktadır — ortanca gelir artışı +%{pct}.",
    },
    tools: {
      noTools: "Henüz araç önerisi mevcut değil.",
      summarySelected: "Seçilen Araçlar",
      summaryImplemented: "Uygulanan",
      summaryInProgress: "Devam Ediyor",
      summaryTopPicks: "🔥 En İyiler",
      setupGuide: "Kurulum Kılavuzu",
      gotchas: "Yaygın Hatalar",
      successStory: "Gerçek Dünya Sonucu",
      whyPicked: "Bunu {businessName} için neden seçtik",
      pricing: "Fiyatlandırma",
      integration: "Entegrasyon",
      alternatives: "Alternatifler",
      statusNotStarted: "Başlanmadı",
      statusInProgress: "Devam Ediyor",
      statusDone: "Tamamlandı ✓",
      verifyPricing: "Güncel fiyatları doğrula",
      details: "Detaylar",
      less: "Daha Az",
    },
    diagnosis: {
      title: "İşletme Sorun Noktası Tanısı",
      costOfInaction: "Tahmini Hareketsizlik Maliyeti",
      priorityIssues: "Öncelikli Sorunlar",
      fixableProblems: "Çözülebilir Sorunlar",
      processing: "Tanı verileri işleniyor. Lütfen yenile.",
      solvedLabel: "bu planla çözüldü",
      perMonth: "/ay",
      calculating: "Hesaplanıyor...",
    },
    prompts: {
      title: "Özel Prompt Kütüphaneniz",
      empty: "Henüz prompt oluşturulmadı.",
      emptyDesc: "Prompt İşletim Sisteminizin kilidini açmak için denetiminizi tamamlayın.",
      export: "Tüm Oyunları Dışa Aktar",
      copy: "Çalıştırmaya Hazır Prompt'u Kopyala",
      copyPersonalized: "Kişiselleştirilmiş Prompt'u Kopyala",
      copied: "Kopyalandı!",
      searchPlaceholder: "Sonuç, departman veya anahtar kelimeye göre ara...",
      noResults: "Hiçbir Prompt Oyunu filtrelerinizle eşleşmiyor.",
      clearFilters: "Filtreleri temizle",
      proTip: "Pro İpucu:",
      runIn: "Bu oyunu şurada çalıştır:",
      whyWorks: "Bu prompt sizin gibi işletmeler için neden işe yarıyor",
      logOutcome: "Sonucunuzu kaydedin (isteğe bağlı)",
    },
    financials: {
      total3Year: "Toplam 3 Yıllık Net Fayda",
      paybackPeriod: "Geri Ödeme Süresi",
      projection: "3 Yıllık Finansal Projeksiyon",
      tableYear: "Yıl",
      tableInvestment: "Yatırım",
      tableReturns: "Getiriler",
      tableNet: "Net Fayda",
      tableROI: "YG",
      howYouCompare: "Nasıl Karşılaştırıyorsunuz",
      kpiTitle: "Haftalık Ölçülecekler",
      kpiDownload: "KPI Takipçisini İndir (CSV)",
      sensitivity: "Duyarlılık Analizi",
      cumulative: "Birikimli Net Fayda",
      peerBusinesses: "Sizin Gibi İşletmeler Seçti",
    },

    referral: {
      heading: "{industry} işletmelerinin çoğunun önüne geçtiniz.",
      subheading: "Yapay zeka konusunda endişelenen başka bir işletme sahibi tanıyor musunuz? Onları buraya gönderin — bu blueprint işlerini kurtarabilecek şey olabilir.",
      cta: "Bir işletme sahibiyle paylaş",
      copied: "Bağlantı kopyalandı!",
      note: "Kayıt gerekmez · 5 dakika · $149 blueprint",
    },

    // NEW: playbooks section
    playbooks: {
      title: "Yapay zeka playbook’ların",
      subtitle:
        "Önümüzdeki 30–90 günde hayata geçirebileceğin yüksek etkili iş akışları.",
      impactsDeptLabel:
        "Bu departmanı etkileyen playbook’lar",
    },

    // NEW: 90‑day checklist section
    checklist: {
      title: "90 günlük adaptasyon checklist’i",
      subtitle:
        "İlk 3 ay için haftalık skor tahtan olarak kullan.",
      categoryLabel: "Kategori",
      weekLabel: "{{week}}. hafta",
    },

    // NEW: governance section
    governance: {
      bannerTitle: "Yapay zeka yönetişimi ve etik",
      bannerSubtitle:
        "{{industry}} sektörü için uyarlanmış yönetişim çerçevesi.",
      riskTitle: "Dikkat edilmesi gereken risk alanları",
      structureTitle: "Yönetişim yapısı",
      oversightBody: "Gözetim ekibi",
      departmentChampions: "Departman sorumluları",
      decisionFramework: "Karar çerçevesi",
    },

    // NEW: guide section
    guide: {
      howToUsePlaybookLabel: "Playbook:",
      howToUsePlaybookText:
        "Her adımı bir sorumluya ata, takvime ekle ve ilerlemeyi haftalık olarak gözden geçir. Pazartesi planla, salı–perşembe uygula, cuma günü değerlendir.",
      headerTitle: "Uygulama rehberin",
      headerSubtitle:
        "İlerlemeni hafta hafta takip et. Adımları tamamlandıkça işaretle, ilerlemen otomatik kaydedilsin.",
      headerProgressLabel: "Genel ilerleme",
      headerStepsLabel: "{{completed}}/{{total}} adım",
      weekByWeekTitle: "Hafta bazlı plan",
      weekLabel: "{{week}}. hafta",
      currentLabel: "Güncel",
      anchoredIn: "Bağlı olduğu faz: {{phase}}",
      weeklyFocusFallback: "Kritik iş akışlarını hayata geçir",
      quarterlyReviewsTitle: "Üç aylık değerlendirmeler",
      suggestedKpisTitle: "Önerilen KPI’lar",
      exportCsv: "CSV olarak dışa aktar",
      shareProgress: "İlerlemeyi paylaş (beta)",
      resourcesLabel: "Kaynaklar",
      ownerLabel: "Sorumlu",
      estimatedTimeLabel: "Tahmini",
    },
  },

  seo: {
    title: "SEO Optimizasyon Kiti",
    included: "Dahil",
    metaTags: "Meta Etiketleri",
    titleTag: "Başlık Etiketi",
    metaDescription: "Meta Açıklaması",
    openGraph:
      "Open Graph Etiketleri",
    headings:
      "Optimize Edilmiş Başlıklar",
    mainHeadline: "H1 (Ana Başlık)",
    h2Suggestions: "H2 Önerileri",
    h3Suggestions: "H3 Önerileri",
    faqSchema:
      "FAQ Schema (SEO için Hazır)",
    blogOutline: "Blog Yazısı Taslağı",
    suggestedTitle: "Önerilen Başlık",
    outline: "Taslak",
    targetKeywords:
      "Hedef Anahtar Kelimeler",
    keywordResearch:
      "Anahtar Kelime Araştırması",
    primaryKeywords:
      "Birincil Anahtar Kelimeler",
    longTailKeywords:
      "Uzun Kuyruk Anahtar Kelimeler",
    trendingTopics:
      "Trend Konular",
    internalLinking:
      "İç Bağlantı Önerileri",
  },

  footer: {
    tagline:
      "AI destekli içgörülerle işletmeleri dönüştürüyoruz",
    company: "Şirket",
    resources: "Kaynaklar",
    legal: "Yasal",
    privacy: "Gizlilik Politikası",
    terms: "Hizmet Şartları",
    copyright:
      "© 2026 AI Workshop LLC. Tüm hakları saklıdır.",
  },

  actions: {
    submit: "Gönder",
    cancel: "İptal",
    back: "Geri",
    next: "İleri",
    loading: "Yükleniyor...",
    success: "Başarılı!",
    error: "Hata",
    tryAgain: "Tekrar Dene",
  },

  // Discover Sayfası
  discover: {
    heroBadge: "Discover · AI Workshop Tool Engine tarafından sunulur",
    heroTitleLine1: "Her hafta tek bir",
    heroTitleLine2: "yüksek kaldıraçlı AI hamlesi",
    heroSubtitle:
      "Özel Tool Engine'imiz canlı RSS sinyallerini, {{count}} seçilmiş AI aracını ve 2026 sektör ROI verisini analiz ederek 300 uygulamalık bir dizin yerine tek bir net öneri sunar.",
    heroPillSignals: "Canlı sinyal takibi",
    heroPillTools: "{{count}} seçilmiş AI aracı",
    heroPillMatching: "Problem–araç eşleştirmesi",

    pickSectionTitle: "Bu haftanın motor seçimi",
    pickLoading: "Bu haftanın en yüksek kaldıraçlı aracını benimseme veritabanımızdan çekiyoruz…",
    pickErrorBody: "Bu haftanın seçimi yüklenemedi. Denetiminizi yapın, size özel tam bir stack çıkarın.",
    pickErrorCta: "AI Denetimine Başla",
    pickEmptyTitle: "Çok yakında: ilk AI Workshop seçiminiz",
    pickEmptyBody:
      "Her hafta KOBİ'lere özel bir AI aracını veya otomasyon hamlesini, somut uygulama adımlarıyla öne çıkaracağız.",
    pickWeek: "Hafta {{week}} · Motor Seçimi",
    pickTrendingUp: "Yükselişte",
    pickUpdatedMonday: "Pazartesi güncellenir",
    pickSmbScoreLabel: "KOBİ Puanı",
    pickEngineTake: "Motorun bu haftaki yorumu",
    pickMetricPricing: "Fiyatlandırma",
    pickMetricPricingDefault: "Değişken",
    pickMetricSetup: "Kurulum",
    pickMetricSetupDefault: "Düşük–Orta",
    pickMetricSetupTime: "Kurulum süresi",
    pickMetricClaimedRoi: "Belirtilen ROI",
    pickMetricLiveSignals: "Canlı sinyaller",
    pickBestFor: "Şunlar için ideal:",
    pickDefaultRole: "Sahip / Operatör",
    pickCtaFit: "Yol haritanıza nasıl oturduğunu görün",
    pickCtaBlueprint: "Tam Blueprint'imi İstiyorum",

    engineTitle: "Motor nasıl seçim yapar",
    engineFootnote:
      "Seçim ISO haftasına göre deterministtir (seed = yıl × 100 + hafta) · Her Pazartesi UTC'de döner",
    engineRssLabel: "Canlı RSS Sinyalleri",
    engineRssDesc:
      "Sektör akışları, ürün lansmanları ve KOBİ forumlarından gerçek zamanlı benimseme sinyalleri",
    engineDataLabel: "KOBİ Benimseme Verisi",
    engineDataDesc:
      "348+ seçilmiş AI aracı + 2026 McKinsey ve Gartner kıyaslamaları — sektöre ve ölçeğe göre gerçekten işe yarayan araçlar",
    engineMatchLabel: "Problem–Araç Eşleşmesi",
    engineMatchDesc:
      "Jaccard token skorlaması, gerçek KOBİ sancılarını araç yetenekleriyle eşler",
    engineRoiLabel: "ROI ve Karmaşıklık Puanı",
    engineRoiDesc:
      "Her araç ROI kanıtı, kurulum kolaylığı ve entegrasyon uyumu üzerinden puanlanır",

    recentTitle: "Motorumuzun yakın zamanda keşfettikleri",
    recentBadge: "RSS'ten canlı",
    recentRoiClaimed: "%{{pct}} ROI iddiası",

    catalogTitle: "AI Workshop Araç Kataloğu",
    catalogBadge: "KOBİ puanına göre sıralı",
    catalogSearchPlaceholder: "Araçları isme göre ara…",
    catalogIndustryAll: "Tümü",
    catalogIndustryRetail: "Perakende",
    catalogIndustryHealthcare: "Sağlık",
    catalogIndustryFinance: "Finans",
    catalogIndustryHospitality: "Otelcilik",
    catalogIndustryManufacturing: "Üretim",
    catalogIndustryLegal: "Hukuk",
    catalogEmpty: "Bu filtre için araç bulunamadı.",
    catalogBadgeGrowing: "Yükselişte",
    catalogBadgeSetup: "{{level}} kurulum",
    catalogBadgeSignals: "{{count}} sinyal",
    catalogBadgeSignalsPlural: "{{count}} sinyal",
    catalogLinkVisit: "Ziyaret et",
    catalogLinkSearch: "Ara",
    catalogAddToStack: "Stack'e ekle",
    catalogRemoveFromStack: "Stack'ten çıkar",
    catalogAddAria: "{{name}} aracını stack'e ekle",
    catalogRemoveAria: "{{name}} aracını stack'ten çıkar",

    catalogCtaTitle:
      "{{count}}+ araca göz atmak ücretsiz. SİZİN işinize hangilerinin uyduğunu bilmek — asıl zor kısım bu.",
    catalogCtaBody:
      "Motorumuz her aracı sektörünüze, sorunlarınıza, bütçenize ve ekip büyüklüğünüze göre puanlar — ardından kurulum kılavuzları, ROI projeksiyonları ve özel promptlarla adım adım bir uygulama planı çıkarır. 5 dakika sürer.",
    catalogCtaButton: "Kişiselleştirilmiş Blueprint'imi Al",
    catalogCtaFine: "$149 tek seferlik · 60 günlük uygulama garantisi",

    nudgeTitle: "Beğendiniz mi? İşinize özel öneriler alın.",
    nudgeBody:
      "Bunlar KOBİ puanına göre sıralanmış genel araçlar. Kişiselleştirilmiş bir blueprint; sektörünüzü, sorunlarınızı ve bütçenizi analiz ederek doğru stack'i önerir — ROI projeksiyonları, kurulum adımları ve 90 günlük planla birlikte.",
    nudgeCta: "Kişiselleştirilmiş Blueprint'imi Al",

    stackTitle: "Kendi Stack'inizi Kurun",
    stackSelected: "{{count}}/3 seçildi",
    stackComplete: "Stack tamamlandı!",
    stackQuickStart: "Hızlı başlangıç:",
    stackFullToast: "Stack dolu — önce bir aracı çıkarın",
    stackEmptyLine1Prefix: "Katalogdaki herhangi bir araçta",
    stackEmptyLine1Suffix: "işaretine tıklayın veya yukarıdaki hızlı başlangıç şablonlarını kullanın",
    stackEmptyLine2: "En fazla 3 araç seçin — 12 haftalık etkiyi biz hesaplarız",
    stackSynergy:
      "Bu araçlar birbirine yerel olarak entegre — stack'iniz kutudan çıktığı gibi çalışır.",
    stackDiversityWarning:
      "Tüm araçlarınız aynı alanı kapsıyor — otomasyon veya iletişim için bir araç eklemeyi düşünün.",
    stackPhase1: "1–4. Hafta",
    stackPhase2: "5–8. Hafta",
    stackPhase3: "9–12. Hafta",
    stackSlotEmpty: "Boş — yukarıdan bir araç ekleyin",
    stackImpactTitle: "12 haftalık tahmini etki",
    stackImpactHours: "Haftada kazanılan saat",
    stackImpactValue: "12 haftalık değer",
    stackImpactValueTooltip:
      "Ortalama $35/saat işgücü maliyetine göre tahmini · işinize göre ayarlayın",
    stackImpactScore: "Ortalama KOBİ puanı",
    stackImpactComplexity: "Karmaşıklık",
    stackImpactNote:
      "* Medyan KOBİ benimseme verilerine dayalı tahmindir — gerçek sonuçlar işletme büyüklüğüne ve uygulamaya göre değişir.",
    stackFinalPrompt: "İşletmenize birebir özel bir stack ister misiniz?",
    stackFinalCta: "Tam Blueprint'imi İstiyorum",
    stackComplexityLow: "Düşük",
    stackComplexityMedium: "Orta",
    stackComplexityHigh: "Yüksek",

    tplRestaurantLabel: "Restoran Hızlı Kazanç",
    tplRestaurantT1Why: "Otomatik SMS hatırlatmalarıyla no-show oranını ~%38 düşürür.",
    tplRestaurantT2Why: "En sık sorulan 10 soruyu otomatik yanıtlar, ekibiniz salondan ayrılmaz.",
    tplRestaurantT3Why: "Pazartesi atılan tek bir e-posta, sessiz Salı akşamlarını doldurur.",
    tplEcomLabel: "E-ticaret Büyümesi",
    tplEcomT1Why: "Terk edilen sepetlerin ~%15'ini otomatik geri kazanır.",
    tplEcomT2Why: "AI, satış öncesi soruları 7/24 yanıtlar ve dönüşümü artırır.",
    tplEcomT3Why: "SMS'te açılma oranı %98, e-postada %20.",
    tplServiceLabel: "Hizmet İşletmesi",
    tplServiceT1Why: "Her lead'i elektronik tabloya gerek kalmadan takip eden ücretsiz CRM.",
    tplServiceT2Why: "Randevu için mesaj trafiğini tamamen ortadan kaldırır.",
    tplServiceT3Why: "HubSpot + Calendly'yi bağlar — hiçbir şey gözden kaçmaz.",
    tplDentalLabel: "Diş Kliniği",
    tplDentalT1Why: "Arama + SMS + hatırlatma merkezi — gelmemeleri %40–60 azaltır.",
    tplDentalT2Why: "Mesai dışı çağrılara cevap verir ve 24/7 randevu alır.",
    tplDentalT3Why: "Ayrılan hastaları otomatik geri kazanır — %20–30'u geri döner.",
    tplLegalLabel: "Avukatlık Ofisi",
    tplLegalT1Why: "Dava yönetimi + zaman takibi + faturalama tek yerde.",
    tplLegalT2Why: "Mesai dışı potansiyel müşterileri yakalar ve siz aramadan önce niteler.",
    tplLegalT3Why: "Sözleşmeleri 3–5× daha hızlı inceler. MS Word içinde çalışır.",
    tplAccountingLabel: "Muhasebe Firması",
    tplAccountingT1Why: "Merkezi defter — tüm sistemin bağlandığı muhasebe omurgası.",
    tplAccountingT2Why: "AI makbuz ve fatura okur, otomatik kategorize eder — haftada 5–8 saat kazanç.",
    tplAccountingT3Why: "Müşteri iletişimi + görev otomasyonu + vergi dönemi kontrol listeleri.",
    tplHvacLabel: "HVAC Müteahhidi",
    tplHvacT1Why: "Sevkiyat + faturalama + müşteri kayıtları — sektör standardı.",
    tplHvacT2Why: "Siz çatıdayken çağrılara cevap verir. Sevkiyatı otomatik ayarlar.",
    tplHvacT3Why: "Her işten sonra otomatik yorum talep eder — itibar birikir.",
    tplMedSpaLabel: "Güzellik Merkezi / Salon",
    tplMedSpaT1Why: "Rezervasyon + müşteri profilleri + gelir raporları tek platformda.",
    tplMedSpaT2Why: "Siz müşteriyle ilgilenirken randevu alır — yeniden planlamayı halleder.",
    tplMedSpaT3Why: "Otomatik yeniden rezervasyon + SMS kampanyaları + yorum talepleri.",

    valuePropsTitle1: "Net bir AI Blueprint'iniz",
    valuePropsDesc1:
      "Neyden başlayacağınızı tahmin etmeyi bırakın. İşletme profilinize eşleştirilmiş {{count}} seçilmiş AI aracından kurulmuş adım adım bir aksiyon planı alın.",
    valuePropsTitle2: "Haftalar içinde kanıtlanmış sonuçlar",
    valuePropsDesc2:
      "Fazlı planı takip eden işletme sahiplerinin çoğu 4 hafta içinde ölçülebilir zaman tasarrufu ve gelir etkisi görür.",
    valuePropsTitle3: "Gerçek veriye dayalı",
    valuePropsDesc3:
      "Velocity Framework'ümüz; 2026 McKinsey ve Gartner kıyaslamalarını kullanarak {{count}} AI aracını profilinize göre puanlar ve size gerçekten işe yarayan araçlar, zaman çizelgeleri ve ROI projeksiyonları verir.",
    valuePropsCta: "AI Blueprint'inizi Alın",

    emailTitle: "Motor seçimini e-postanıza alın",
    emailBody:
      "Her hafta seçilmiş bir AI aracı veya otomasyon hamlesi — yol haritanızdaki yeriyle birlikte. Spam yok, istediğiniz zaman çıkabilirsiniz.",
    emailPlaceholder: "siz@sirketiniz.com",
    emailSubmit: "Discover listesine katıl",
    emailSubmitting: "Ekleniyor…",
    emailFine: "Her Pazartesi gönderilir · Spam yok · İstediğiniz zaman çıkabilirsiniz",
    emailSuccessToast: "Discover listesindesiniz! Gelen kutunuzu kısa sürede kontrol edin.",
    emailErrorToast: "Bir şeyler ters gitti — tekrar deneyin veya bize doğrudan e-posta atın.",
  },
}, // closes tr
}; // closes translations



// Language names for display
export const languageNames = {
  en: { name: "English", flag: "🇺🇸" },
  es: { name: "Español", flag: "🇪🇸" },
  tr: { name: "Türkçe", flag: "🇹🇷" },
};


// Get nested translation by dot notation
export const getTranslation = (lang, key) => {
  const keys = key.split(".");
  let result = translations[lang] || translations.en;

  for (const k of keys) {
    result = result?.[k];
    if (result === undefined) {
      // Fallback to English
      result = translations.en;
      for (const k2 of keys) {
        result = result?.[k2];
      }
      break;
    }
  }

  return result || key;
};

// Simple interpolation helper — supports both {key} and {{key}} patterns
export const interpolate = (template, vars = {}) => {
  if (typeof template !== "string") return template;
  return Object.keys(vars).reduce((str, key) => {
    // Match {{key}} first, then {key} (single-brace not preceded/followed by another brace)
    const re = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}|\\{\\s*${key}\\s*\\}`, "g");
    return str.replace(re, String(vars[key] ?? ""));
  }, template);
};

// Combined translate + interpolate
export const tWithVars = (lang, key, vars) => {
  const raw = getTranslation(lang, key);
  return interpolate(raw, vars);
};

export default translations;
